import { Link, useSearchParams } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AppShellV8 } from "../AppShellV8";
import Button from "@/ui/Button";
import { ButtonTypes } from "@/ui/types";
import { DOCS_API_DOMAIN } from "@launchos/shared-utils/env";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm"; // GFM tables — without this, pipe tables render as raw text
import { agentsMarkdownComponents as markdownComponents } from "../markdown";
import { getActiveClient, useActiveClient, getCurrentMemberId } from "../activeClient";
import { canApprove, isMine, reviewTransition, type ReviewState } from "../reviewState";
import { isAdmin, useRoster, useEnsureRoster, memberById, getActiveRoster } from "../roster";
import { personFor } from "../people";
import { SetForCampaignPicker, type CampaignAssignment } from "../SetForCampaignPicker";
import { EntityPicker } from "../EntityPicker";


/* ═══════════════════════════════════════════════════════════════════════════
   TYPES
   ═══════════════════════════════════════════════════════════════════════════ */

type ReviewKind = "asset" | "decision";
type Urgency = "urgent" | "overdue" | "due-today" | "upcoming" | "no-date";
type RoleView = "my" | "all";

interface Person {
  initials: string;
  name: string;
  color: string;
}

interface DecisionOption {
  id?: string;
  label: string;
  summary: string;
  recommended: boolean;
}

interface RevisionEntry {
  version: number;
  change: string;
  by: string;
  when: string;
}

interface MeetingRef {
  title: string;
  date: string;
  href: string;
}

interface ReviewItem {
  id: number;
  slug?: string; // operator-API deliverable id (real data); absent on mock items
  title: string;
  kind: ReviewKind;
  client: string;
  clientInitials: string;
  clientColor: string;
  campaign: string;
  owner: Person;
  approvers: Person[];
  submittedBy?: string | null; // member slug who set it for review (D7 routing)
  reviewer?: string | null;    // member slug it's routed to (falls back to first approver)
  reviewState?: string;        // in-review | approved | changes-requested (deliverable) or pending/status (decision)
  comments?: { author: string; at: string; text: string }[];
  dueDate: string;
  difficulty: number; // 1-5
  urgency: Urgency;
  deliverableCount: number;
  unreviewed: boolean;

  // S.I.R. content
  situation: string;
  impact: string; // B2: why it matters (shown between Situation and Recommendation)
  statement: string; // B2: a note prepended before the asset (the Recommendation)
  information: string[];
  recommendation: string; // for assets: preview text
  deliverablePreview?: string; // thumbnail placeholder description
  decisionOptions?: DecisionOption[]; // for decisions

  // Trail
  meetings: MeetingRef[];
  revisions: RevisionEntry[];
}

// Editable shape for the Create / Edit decision modal
interface DecisionDraft {
  title: string;
  clientInitials: string;
  campaign: string;
  ownerInitials: string;
  urgency: Urgency;
  situation: string;
  information: string; // newline-separated bullets
  recommendation: string;
}

/* ═══════════════════════════════════════════════════════════════════════════
   PEOPLE — tenant-aware (AMB-499)
   Owner/assignee values resolve against the SIGNED-IN tenant's roster, not a built-in Ambid
   list. personFor (people.ts) reads getActiveRoster() at call time; slugForInitials maps a
   picked initials back to that member's id for persistence. Ambid (no operator roster) falls
   back to the built-in ROSTER unchanged. Assignee option lists come from useRoster() in the
   components below (so they re-render once the tenant roster loads).
   ═══════════════════════════════════════════════════════════════════════════ */

// Map a picked member's initials back to their roster id, for persisting the assignee slug.
const slugForInitials = (initials: string): string =>
  getActiveRoster().find((m) => m.initials === initials)?.id ?? initials.toLowerCase();

const REVIEW_CLIENT = getActiveClient();

const titleCaseSlug = (s?: string | null) =>
  (s || "").split("-").filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");

const urgencyFromDue = (due?: string): Urgency => {
  if (!due) return "no-date";
  const d = new Date(`${due}T00:00:00`);
  if (isNaN(d.getTime())) return "no-date";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (d.getTime() < today.getTime()) return "overdue";
  if (d.getTime() === today.getTime()) return "due-today";
  return "upcoming";
};

interface ApiReview {
  id: string;
  title: string;
  deliverableType?: string;
  campaign?: string;
  owner?: string;
  approvers?: string[];
  due?: string;
  difficulty?: number;
  reviewState?: string;
  submittedBy?: string | null;
  reviewer?: string | null;
  comments?: { author: string; at: string; text: string }[];
  preview?: string;
  body?: string;
  sir?: { situation?: string; impact?: string; statement?: string; information?: string[]; recommendation?: string } | null;
  sourceMeetingDetails?: { id: string; title: string; date?: string | null }[];
}

// Short "Mon D" date for the source trail.
const shortDate = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const apiToReviewItem = (item: ApiReview, idx: number): ReviewItem => ({
  id: 9000 + idx,
  slug: item.id,
  title: item.title,
  kind: "asset",
  client: REVIEW_CLIENT.name,
  clientInitials: REVIEW_CLIENT.initials,
  clientColor: REVIEW_CLIENT.color,
  campaign: titleCaseSlug(item.campaign),
  owner: personFor(item.owner),
  approvers: (item.approvers || []).map((a) => personFor(a)),
  submittedBy: item.submittedBy || null,
  reviewer: item.reviewer || (item.approvers && item.approvers[0]) || null,
  reviewState: item.reviewState || "in-review",
  comments: Array.isArray(item.comments) ? item.comments : [],
  dueDate: item.due || "",
  difficulty: typeof item.difficulty === "number" ? item.difficulty : 3,
  urgency: urgencyFromDue(item.due),
  deliverableCount: 1,
  unreviewed: true,
  // S/I/R is rendered from the deliverable's `sir` frontmatter when present
  // (captured at review-creation time); recommendation falls back to the body.
  situation: item.sir?.situation || "",
  impact: item.sir?.impact || "",
  statement: item.sir?.statement || "",
  information: Array.isArray(item.sir?.information) ? item.sir!.information! : [],
  recommendation: item.sir?.recommendation || item.body || item.preview || "",
  deliverablePreview: `${titleCaseSlug(item.deliverableType) || "Document"} preview`,
  // Source trail = the meeting(s) that produced this deliverable (sourceMeetings).
  // Line-level transcript excerpts + audio remain post-trial.
  meetings: (item.sourceMeetingDetails || []).map((m) => ({
    title: m.title,
    date: shortDate(m.date),
    href: `/agents/digest?id=${encodeURIComponent(m.id)}`,
  })),
  revisions: [],
});

interface ApiDecision {
  id: string;
  reviewType?: string;
  title: string;
  campaign?: string | null;
  urgency?: Urgency;
  due?: string | null;
  difficulty?: number;
  assignee?: string | null;
  approvers?: string[];
  sir?: { situation?: string; impact?: string; statement?: string; information?: string[]; recommendation?: string } | null;
  options?: { id?: string; label: string; summary?: string; recommended?: boolean }[];
  status?: string;
}

const apiToDecisionItem = (item: ApiDecision, idx: number): ReviewItem => ({
  id: 8000 + idx,
  slug: item.id,
  title: item.title,
  kind: "decision",
  client: REVIEW_CLIENT.name,
  clientInitials: REVIEW_CLIENT.initials,
  clientColor: REVIEW_CLIENT.color,
  campaign: titleCaseSlug(item.campaign || ""),
  owner: personFor(item.assignee || undefined),
  approvers: (item.approvers || []).map((a) => personFor(a)),
  submittedBy: null,
  reviewer: item.assignee || (item.approvers && item.approvers[0]) || null,
  // Decisions use status (pending|approved|changes-requested); map to a reviewState-like value.
  reviewState: !item.status || item.status === "pending" ? "in-review" : item.status,
  dueDate: item.due || "",
  difficulty: typeof item.difficulty === "number" ? item.difficulty : 3,
  urgency: item.urgency || urgencyFromDue(item.due || undefined),
  deliverableCount: 0,
  unreviewed: true,
  situation: item.sir?.situation || "",
  impact: item.sir?.impact || "",
  statement: item.sir?.statement || "",
  information: Array.isArray(item.sir?.information) ? item.sir!.information! : [],
  recommendation: item.sir?.recommendation || "",
  decisionOptions: (item.options || []).map((o) => ({ id: o.id, label: o.label, summary: o.summary || "", recommended: !!o.recommended })),
  deliverablePreview: "",
  meetings: [],
  revisions: [],
});

/* ═══════════════════════════════════════════════════════════════════════════
   CLIENT & CAMPAIGN LISTS
   ═══════════════════════════════════════════════════════════════════════════ */

// Single-client trial — the real client. (Multi-client = fetch a roster later.)
const CLIENTS = [
  { initials: REVIEW_CLIENT.initials, name: REVIEW_CLIENT.name, color: REVIEW_CLIENT.color, dotColor: "bg-amber-400" },
];

// Campaign options are fetched from /campaigns at runtime (campaignOptions state);
// this const is just the empty fallback before the fetch resolves.

/* ═══════════════════════════════════════════════════════════════════════════
   REVIEW DATA  (10 items: 7 assets, 3 decisions)
   ═══════════════════════════════════════════════════════════════════════════ */


/* ═══════════════════════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════════════════════ */

const URGENCY_BORDER: Record<Urgency, string> = {
  urgent: "border-l-orange-500",
  overdue: "border-l-red-500",
  "due-today": "border-l-amber-500",
  upcoming: "border-l-emerald-500",
  "no-date": "border-l-gray-300",
};

const URGENCY_TEXT: Record<Urgency, { label: string; color: string }> = {
  urgent: { label: "Urgent", color: "text-orange-600" },
  overdue: { label: "Overdue", color: "text-red-600" },
  "due-today": { label: "Due Today", color: "text-amber-600" },
  upcoming: { label: "Upcoming", color: "text-gray-500" },
  "no-date": { label: "No Date", color: "text-gray-400" },
};

const URGENCY_BADGE: Record<Urgency, string> = {
  urgent: "bg-orange-50 text-orange-600 ring-1 ring-orange-200/60",
  overdue: "bg-red-50 text-red-600 ring-1 ring-red-200/60",
  "due-today": "bg-amber-50 text-amber-600 ring-1 ring-amber-200/60",
  upcoming: "bg-gray-50 text-gray-500 ring-1 ring-gray-200/60",
  "no-date": "bg-gray-50 text-gray-400 ring-1 ring-gray-200/40",
};

const KIND_BADGE: Record<ReviewKind, { label: string; classes: string }> = {
  asset: { label: "Asset", classes: "bg-blue-50 text-blue-600 ring-1 ring-blue-200/60" },
  decision: { label: "Decision", classes: "bg-amber-50 text-amber-600 ring-1 ring-amber-200/60" },
};

// Review-state badge (used in the "My Reviews" author view so submitters see the outcome).
const REVIEW_STATE_BADGE: Record<string, { label: string; classes: string }> = {
  "in-review": { label: "In review", classes: "bg-blue-50 text-blue-600 ring-1 ring-blue-200/60" },
  approved: { label: "Approved", classes: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60" },
  "changes-requested": { label: "Changes requested", classes: "bg-amber-50 text-amber-700 ring-1 ring-amber-200/60" },
};
const reviewStateBadge = (state?: string) =>
  REVIEW_STATE_BADGE[state || "in-review"] || REVIEW_STATE_BADGE["in-review"];

// Latest reviewer comment (the most recently authored one), for the author status line.
const latestComment = (comments?: { author: string; at: string; text: string }[]) => {
  if (!comments || comments.length === 0) return null;
  return comments[comments.length - 1];
};

type TypeFilter = "all" | "asset" | "decision";
type UrgencyFilter = "all" | "urgent" | "overdue" | "due-today" | "upcoming";

const URGENCY_SORT_ORDER: Record<Urgency, number> = {
  urgent: 0,
  overdue: 1,
  "due-today": 2,
  upcoming: 3,
  "no-date": 4,
};

/* ═══════════════════════════════════════════════════════════════════════════
   STATS
   ═══════════════════════════════════════════════════════════════════════════ */

const computeStats = (items: ReviewItem[]) => ({
  pending: items.length,
  urgent: items.filter((r) => r.urgency === "urgent").length,
  overdue: items.filter((r) => r.urgency === "overdue").length,
  dueToday: items.filter((r) => r.urgency === "due-today").length,
});

/* ═══════════════════════════════════════════════════════════════════════════
   ⋯ OVERFLOW MENU  (FIXED/portal-positioned, click-outside backdrop, never clipped)
   ═══════════════════════════════════════════════════════════════════════════ */

interface OverflowMenuItem {
  label: string;
  onClick: () => void;
  destructive?: boolean;
}

function OverflowMenu({
  items,
  anchor,
  onClose,
}: {
  items: OverflowMenuItem[];
  anchor: { top: number; left: number };
  onClose: () => void;
}) {
  if (typeof document === "undefined") return null;
  return createPortal(
    <>
      {/* Click-outside backdrop */}
      <div className="fixed inset-0 z-[60]" onClick={onClose} />
      <div
        className="fixed z-[61] min-w-[160px] rounded-lg bg-white py-1 text-[13px] shadow-xl ring-1 ring-black/[0.06]"
        style={{ top: anchor.top, left: anchor.left }}
        onClick={(e) => e.stopPropagation()}
      >
        {items.map((it) => (
          <button
            key={it.label}
            onClick={() => {
              it.onClick();
              onClose();
            }}
            className={`flex w-full items-center gap-2 px-3 py-1.5 text-left transition-all duration-150 hover:bg-gray-100 ${
              it.destructive ? "text-red-600 hover:bg-red-50" : "text-gray-700"
            }`}
          >
            {it.label}
          </button>
        ))}
      </div>
    </>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CREATE / EDIT DECISION MODAL
   ═══════════════════════════════════════════════════════════════════════════ */

const URGENCY_CHOICES: Urgency[] = ["urgent", "overdue", "due-today", "upcoming", "no-date"];

function DecisionModal({
  mode,
  initial,
  campaigns,
  onCancel,
  onSave,
}: {
  mode: "create" | "edit";
  initial: DecisionDraft;
  campaigns: string[];
  onCancel: () => void;
  onSave: (draft: DecisionDraft) => void;
}) {
  const [draft, setDraft] = useState<DecisionDraft>(initial);
  // The signed-in tenant's roster drives the Assignee options (AMB-499), so a new decision
  // never lists another agency's people.
  const roster = useRoster();
  if (typeof document === "undefined") return null;

  const set = <K extends keyof DecisionDraft>(k: K, v: DecisionDraft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const inputCls =
    "w-full rounded-lg border border-black/[0.06] bg-white px-3 py-2 text-[13px] text-gray-700 placeholder:text-gray-400 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500/30";
  const labelCls = "mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-400";

  return createPortal(
    <>
      <div className="fixed inset-0 z-[70] bg-black/30 backdrop-blur-[2px]" onClick={onCancel} />
      <div className="fixed inset-0 z-[71] flex items-start justify-center overflow-y-auto p-6">
        <div
          className="my-8 w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-black/[0.06] transition-all duration-150"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">
              {mode === "create" ? "Raise a Decision" : "Edit Decision"}
            </h2>
            <button
              onClick={onCancel}
              className="rounded-md p-1 text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-600"
              title="Close"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className={labelCls}>Title</label>
              <input
                className={inputCls}
                value={draft.title}
                onChange={(e) => set("title", e.target.value)}
                placeholder="e.g. Q3 Offer Strategy"
              />
            </div>

            {mode === "create" && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Client</label>
                  <select
                    className={inputCls}
                    value={draft.clientInitials}
                    onChange={(e) => set("clientInitials", e.target.value)}
                  >
                    {CLIENTS.map((c) => (
                      <option key={c.initials} value={c.initials}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Campaign</label>
                  <select
                    className={inputCls}
                    value={draft.campaign}
                    onChange={(e) => set("campaign", e.target.value)}
                  >
                    {campaigns.length === 0 && <option value="">No campaigns</option>}
                    {campaigns.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Assignee</label>
                <select
                  className={inputCls}
                  value={draft.ownerInitials}
                  onChange={(e) => set("ownerInitials", e.target.value)}
                >
                  {roster.map((p) => (
                    <option key={p.id} value={p.initials}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Urgency</label>
                <select
                  className={inputCls}
                  value={draft.urgency}
                  onChange={(e) => set("urgency", e.target.value as Urgency)}
                >
                  {URGENCY_CHOICES.map((u) => (
                    <option key={u} value={u}>{URGENCY_TEXT[u].label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className={labelCls}>Situation</label>
              <textarea
                className={inputCls}
                rows={3}
                value={draft.situation}
                onChange={(e) => set("situation", e.target.value)}
                placeholder="What's the context that requires a decision?"
              />
            </div>

            <div>
              <label className={labelCls}>Information (one point per line)</label>
              <textarea
                className={inputCls}
                rows={4}
                value={draft.information}
                onChange={(e) => set("information", e.target.value)}
                placeholder={"Key fact 1\nKey fact 2\nKey fact 3"}
              />
            </div>

            <div>
              <label className={labelCls}>Recommendation</label>
              <textarea
                className={inputCls}
                rows={2}
                value={draft.recommendation}
                onChange={(e) => set("recommendation", e.target.value)}
                placeholder="Your recommended path forward"
              />
            </div>
          </div>

          <div className="mt-6 flex items-center justify-end gap-2">
            <button
              onClick={onCancel}
              className="rounded-lg border border-black/[0.06] bg-white px-4 py-2 text-[13px] font-medium text-gray-600 transition-all duration-150 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave(draft)}
              className="rounded-lg bg-gray-900 px-4 py-2 text-[13px] font-semibold text-white transition-all duration-150 hover:bg-gray-800 active:scale-[0.97]"
            >
              {mode === "create" ? "Raise Decision" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

export function Reviews() {
  // Filters
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all");
  const [clientFilter, setClientFilter] = useState<string>("all");
  const [campaignFilter, setCampaignFilter] = useState<string>("all");
  const [urgencyFilter, setUrgencyFilter] = useState<UrgencyFilter>("all");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("all");
  const [roleView, setRoleView] = useState<RoleView>("my");
  // The active client, read reactively so switching to "All Clients" (or any client)
  // re-queries the Review Center instead of being frozen at page-load (the old module-level
  // clientSlug never updated without a hard reload — why sent items "vanished").
  const clientSlug = useActiveClient().slug;

  // Resolve the signed-in member from the canonical roster (AMB-358) so "my" buckets by
  // routing. null = an unrecognized login → no "my" items, never silently "kyle".
  const currentMemberId = getCurrentMemberId();
  // The signed-in TENANT's roster (AMB-499) — drives the assignee options + the default
  // owner/approver for a new decision. Falls back to the built-in ROSTER for the Ambid
  // dogfood. defaultOwner = the current member (you), never a hardcoded Kyle.
  useEnsureRoster();
  const roster = useRoster();
  const defaultOwner = memberById(currentMemberId, roster) ?? roster[0];
  // C2: which review (if any) has the Set-for-Campaign picker open.
  const [campaignPickFor, setCampaignPickFor] = useState<number | null>(null);

  // Filters popover
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Expanded card — inline expand-in-place is retired (D1: every card opens the
  // slide-out panel). Kept as null so no card renders the legacy inline detail.
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [selectedOption, setSelectedOption] = useState<Record<number, number>>({});
  // D4 free-form path: -1 in selectedOption = "Other / write-in"; decisionAnswer holds the text.
  const [decisionAnswer, setDecisionAnswer] = useState<Record<number, string>>({});

  // Slide-out panel
  const [slideOutId, setSlideOutId] = useState<number | null>(null);

  // Queue-clear state (shown briefly after approving the last item)
  const [queueCleared, setQueueCleared] = useState(false);

  // Mobile preview
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(false);

  // Slide-out panel always renders fully expanded (D2 — size toggle removed).
  const panelExpanded = true;
  // D3: composer for posting a plain comment (decoupled from Request Changes).
  const [slideOutCommentDraft, setSlideOutCommentDraft] = useState("");
  // Real document body for the open review (asset reviews are deliverables) — fetched
  // so the expanded panel shows the actual content, not a keyword-built mockup.
  const [slideBody, setSlideBody] = useState<string | null>(null);
  useEffect(() => {
    if (slideOutId === null) { setSlideBody(null); return; }
    const item = reviews.find((r) => r.id === slideOutId);
    if (!item) { setSlideBody(null); return; }
    // Decisions have no deliverable document body — the panel renders their options
    // voting UI instead, so skip the (doomed) deliverable fetch.
    if (item.kind === "decision") { setSlideBody(""); return; }
    let cancelled = false;
    setSlideBody(null);
    fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/${encodeURIComponent(item.id)}?client=${clientSlug}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setSlideBody(typeof d?.body === "string" ? d.body : ""); })
      .catch(() => { if (!cancelled) setSlideBody(""); });
    return () => { cancelled = true; };
  }, [slideOutId]);

  // Action states
  const [showChangesFor, setShowChangesFor] = useState<number | null>(null);
  const [changeComment, setChangeComment] = useState("");
  const [applyUniversally, setApplyUniversally] = useState(false);
  // AMB-390: send-back recipients are now multi (slug arrays), chosen via the SAME shared
  // EntityPicker as Send-for-Review (DRY). recipientPicker says which surface's picker is open.
  const [sendToRecipients, setSendToRecipients] = useState<string[]>([]);
  const [slideOutRecipients, setSlideOutRecipients] = useState<string[]>([]);
  const [recipientPicker, setRecipientPicker] = useState<null | "card" | "slideout">(null);
  const [showSourceFor, setShowSourceFor] = useState<number | null>(null);
  const [showHistoryFor, setShowHistoryFor] = useState<number | null>(null);
  const [slideOutShowChanges, setSlideOutShowChanges] = useState(false);
  const [slideOutComment, setSlideOutComment] = useState("");
  const [slideOutApplyUniversally, setSlideOutApplyUniversally] = useState(false);

  /* ── CRUD state ──────────────────────────────────────────────────────────
     Reviews now live in local React state so we can create / edit / soft-delete.
     `archived` is a soft-delete Set that removes items from the visible queue. */
  const [reviews, setReviews] = useState<ReviewItem[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [archived, setArchived] = useState<Set<number>>(new Set());
  const [toast, setToast] = useState<{ message: string; undo: () => void } | null>(null);
  // Real campaign names for the filter + "Raise a Decision" modal dropdowns.
  const [campaignOptions, setCampaignOptions] = useState<string[]>([]);

  // ⋯ overflow menu: which item is open + where to anchor the fixed popover
  const [menuFor, setMenuFor] = useState<number | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  // Create / Edit modal
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const nextIdRef = useRef(1000);

  // Auto-dismiss the toast after a few seconds
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  // Load the real review queue from the operator API (transcript-ingest). Replaces
  // the mock data when it returns; on failure/empty we keep the mock as a fallback.
  useEffect(() => {
    let cancelled = false;
    fetch(`${DOCS_API_DOMAIN}/api/agents/reviews?client=${clientSlug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (cancelled || !data) return;
        const assets = (data.items || []).map(apiToReviewItem);
        const decisions = (data.decisions || []).map(apiToDecisionItem);
        // Always reflect the real queue for the active client — including empty (so a
        // client with no reviews shows empty, not a stale list). Network failure → catch
        // keeps whatever's there.
        setReviews([...assets, ...decisions]);
      })
      .catch(() => {/* keep current list on network failure */})
      .finally(() => { if (!cancelled) setReviewsLoading(false); });
    return () => {
      cancelled = true;
    };
  }, [clientSlug]);

  // Deep-link: /agents/reviews?open=<slug> auto-opens that review's slide-out once reviews
  // load. Applied once (openUrlAppliedRef) so it doesn't re-open after the user closes it.
  const [searchParams] = useSearchParams();
  const openUrlAppliedRef = useRef(false);
  useEffect(() => {
    if (openUrlAppliedRef.current || !reviews.length) return;
    const wanted = searchParams.get("open");
    if (wanted) {
      const found = reviews.find((r) => r.slug === wanted);
      if (found) setSlideOutId(found.id);
    }
    openUrlAppliedRef.current = true;
  }, [reviews, searchParams]);

  // Load real campaign names for the filter + decision-modal dropdowns.
  useEffect(() => {
    let cancelled = false;
    fetch(`${DOCS_API_DOMAIN}/api/agents/campaigns?client=${clientSlug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => {
        if (cancelled || !data?.items) return;
        // Derive from the campaign id (slug) so these match the reviews' campaign
        // label (apiToReviewItem uses titleCaseSlug on the same slug).
        setCampaignOptions(data.items.map((c: { id: string }) => titleCaseSlug(c.id)));
      })
      .catch(() => {/* leave empty */});
    return () => {
      cancelled = true;
    };
  }, [clientSlug]);

  // ── Soft-delete (Archive / Dismiss) ──
  const archiveItem = (id: number, verb: "Archived" | "Dismissed") => {
    setArchived((prev) => new Set(prev).add(id));
    // Clean up any open views referencing the now-hidden item
    if (expandedId === id) setExpandedId(null);
    if (slideOutId === id) { setSlideOutId(null); }
    setToast({
      message: `${verb} · Undo`,
      undo: () =>
        setArchived((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        }),
    });
  };

  // Open the ⋯ menu anchored below-right of the clicked button
  const openMenu = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setMenuAnchor({ top: rect.bottom + 4, left: Math.max(8, rect.right - 160) });
    setMenuFor(id);
  };

  // Create a new decision-type review → prepend to the queue
  const createDecision = (draft: DecisionDraft) => {
    const client = CLIENTS.find((c) => c.initials === draft.clientInitials) ?? CLIENTS[0];
    const owner = roster.find((m) => m.initials === draft.ownerInitials) ?? defaultOwner;
    const newItem: ReviewItem = {
      id: nextIdRef.current++,
      title: draft.title.trim() || "Untitled Decision",
      kind: "decision",
      client: client.name,
      clientInitials: client.initials,
      clientColor: client.color,
      campaign: draft.campaign || campaignOptions[0] || "",
      owner,
      approvers: defaultOwner ? [defaultOwner] : [],
      dueDate: "",
      difficulty: 3,
      urgency: draft.urgency,
      deliverableCount: 0,
      unreviewed: true,
      situation: draft.situation.trim(),
      impact: "",
      statement: "",
      information: draft.information
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean),
      recommendation: draft.recommendation.trim(),
      decisionOptions: [],
      meetings: [],
      revisions: [{ version: 1, change: "Decision raised in Review Center", by: owner.initials, when: "Today" }],
    };
    setReviews((prev) => [newItem, ...prev]);
    setCreateOpen(false);

    // Persist the decision as a type:review file. campaignOptions are title-cased
    // from the slug, so lowercasing + hyphenating recovers the campaign slug.
    const information = newItem.information;
    fetch(`${DOCS_API_DOMAIN}/api/agents/review/create`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        client: clientSlug,
        fields: {
          title: newItem.title,
          campaign: draft.campaign ? draft.campaign.toLowerCase().replace(/\s+/g, "-") : null,
          assignee: slugForInitials(draft.ownerInitials),
          urgency: draft.urgency,
          sir: { situation: newItem.situation, information, recommendation: newItem.recommendation },
        },
      }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d?.id) setReviews((prev) => prev.map((r) => (r.id === newItem.id ? { ...r, slug: d.id } : r)));
      })
      .catch(() => {/* optimistic — stays local on failure */});
  };

  // Save edits to an existing item's editable fields
  const saveEdit = (id: number, draft: DecisionDraft) => {
    const owner = roster.find((m) => m.initials === draft.ownerInitials) ?? defaultOwner;
    const information = draft.information.split("\n").map((s) => s.trim()).filter(Boolean);
    setReviews((prev) =>
      prev.map((r) =>
        r.id === id
          ? {
              ...r,
              title: draft.title.trim() || r.title,
              situation: draft.situation.trim(),
              information,
              recommendation: draft.recommendation.trim(),
              urgency: draft.urgency,
              owner,
            }
          : r
      )
    );
    setEditingId(null);

    // Persist real decision reviews (those with a slug) to the type:review file.
    const item = reviews.find((r) => r.id === id);
    if (item?.kind === "decision" && item.slug) {
      fetch(`${DOCS_API_DOMAIN}/api/agents/entity/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client: clientSlug,
          type: "review",
          id: item.slug,
          fields: {
            title: draft.title.trim() || item.title,
            assignee: slugForInitials(draft.ownerInitials),
            urgency: draft.urgency,
            sir: { situation: draft.situation.trim(), information, recommendation: draft.recommendation.trim() },
          },
        }),
      }).catch(() => {/* optimistic */});
    }
  };

  // Active secondary filter count (for badge on Filters button)
  const activeFilterCount = [clientFilter, campaignFilter, assigneeFilter].filter((f) => f !== "all").length;

  // Visible queue = live reviews minus soft-deleted (archived/dismissed) items
  const visibleReviews = reviews.filter((r) => !archived.has(r.id));
  const STATS = computeStats(visibleReviews);

  // Filter logic
  const filtered = visibleReviews.filter((r) => {
    if (typeFilter !== "all" && r.kind !== typeFilter) return false;
    if (clientFilter !== "all" && r.clientInitials !== clientFilter) return false;
    if (campaignFilter !== "all" && r.campaign !== campaignFilter) return false;
    if (urgencyFilter !== "all" && r.urgency !== urgencyFilter) return false;
    if (assigneeFilter !== "all" && r.owner.initials !== assigneeFilter) return false;
    // "related to me": show only items I review or I submitted (multi-reviewer aware).
    if (roleView === "my" && !isMine(r, currentMemberId)) return false;
    return true;
  }).sort((a, b) => URGENCY_SORT_ORDER[a.urgency] - URGENCY_SORT_ORDER[b.urgency]);

  // ── Queue advance helpers (core-loop throughput) ──
  // Approve the current inline-expanded card and advance expandedId to the next
  // item in `filtered`, wrapping to the first. Scrolls the newly expanded card
  // into view. After the last item, briefly shows a "Queue clear" state.
  // Real approve: for an API-backed item, write reviewState=approved (direct write
  // endpoint) and drop it from the local queue. Mock items (no slug) are left as-is.
  const approveReal = (id: number) => {
    const item = reviews.find((r) => r.id === id);
    if (!item?.slug) return;
    // A4 hard gate: only an assigned reviewer can approve. All approve paths
    // (Approve only, Approve & Next) route through here, so this is the enforcement point.
    if (!canApprove(item, currentMemberId)) return;
    fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: clientSlug, id: item.slug, reviewState: "approved" }),
    }).catch(() => {/* optimistic: UI already advanced */});
    setReviews((prev) => prev.filter((r) => r.id !== id));
  };

  // Request Changes — persist reviewState=changes-requested (+ routedTo recipients) for
  // API-backed asset reviews. Decision reviews (no slug) stay local. `recipientSlugs` are
  // roster slugs from the shared picker (AMB-390); empty defaults to the original submitter.
  const submitChanges = (id: number, recipientSlugs: string[], comment = "") => {
    const item = reviews.find((r) => r.id === id);
    if (item?.kind === "decision") {
      resolveDecision(id, "changes-requested"); // decisions write status+resolution, not deliverable reviewState
      return;
    }
    if (item?.slug) {
      const fields: Record<string, unknown> = { reviewState: "changes-requested" };
      // B3: route to the picked recipient(s), defaulting to the original submitter so the
      // changes go back to whoever raised it unless the reviewer chooses others.
      fields.routedTo = recipientSlugs.length ? recipientSlugs : [item.submittedBy || ""];
      // Persist the reviewer's comment so the author sees what to change (D5).
      if (comment.trim()) fields.comments = [...(item.comments || []), { author: getCurrentMemberId(), at: new Date().toISOString(), text: comment.trim() }];
      fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client: clientSlug, id: item.slug, fields }),
      }).catch(() => {/* optimistic */});
    }
    setReviews((prev) => prev.filter((r) => r.id !== id)); // drops it from the live queue
  };

  // Resolve a decision review: write status + resolution (D4) to the type:review file.
  const resolveDecision = (id: number, status: string) => {
    const item = reviews.find((r) => r.id === id);
    if (item?.slug && item.kind === "decision") {
      const opts = item.decisionOptions || [];
      const idx = selectedOption[id];
      let resolution: Record<string, unknown>;
      if (status === "approved") {
        const writeIn = idx === -1;
        const picked = !writeIn ? (idx != null ? opts[idx] : opts.find((o) => o.recommended)) : undefined;
        resolution = {
          decision: picked?.id ?? null,
          answer: writeIn || opts.length === 0 ? decisionAnswer[id] || "" : null,
          applyUniversally,
          by: "kyle",
          at: new Date().toISOString(),
        };
      } else {
        resolution = { by: "kyle", at: new Date().toISOString(), comment: changeComment || null };
      }
      fetch(`${DOCS_API_DOMAIN}/api/agents/review/resolve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client: clientSlug, id: item.slug, status, resolution }),
      }).catch(() => {/* optimistic */});
    }
    setReviews((prev) => prev.filter((r) => r.id !== id));
  };

  // Reopen — move an approved / changes-requested item back to in-review. Mirrors
  // the approve/request-changes write paths: deliverable reviews POST reviewState,
  // decision reviews POST status=pending to the resolve endpoint. Optimistically
  // flips the local reviewState so the badge updates without a refetch.
  const reopenItem = (id: number) => {
    const item = reviews.find((r) => r.id === id);
    if (!item?.slug) {
      setReviews((prev) => prev.map((r) => (r.id === id ? { ...r, reviewState: "in-review", unreviewed: true } : r)));
      return;
    }
    if (item.kind === "decision") {
      fetch(`${DOCS_API_DOMAIN}/api/agents/review/resolve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client: clientSlug, id: item.slug, status: "pending" }),
      }).catch(() => {/* optimistic */});
    } else {
      fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ client: clientSlug, id: item.slug, reviewState: "in-review" }),
      }).catch(() => {/* optimistic */});
    }
    setReviews((prev) => prev.map((r) => (r.id === id ? { ...r, reviewState: "in-review", unreviewed: true } : r)));
  };

  // B4 — Withdraw: the SUBMITTER pulls their own item back to Draft (reviewState "none").
  // Validated by the state machine (only legal from in-review / changes-requested).
  const withdrawItem = (id: number) => {
    const item = reviews.find((r) => r.id === id);
    if (!item?.slug) return;
    const next = reviewTransition((item.reviewState || "none") as ReviewState, "withdraw");
    if (!next) return; // nothing to withdraw from this state
    fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: clientSlug, id: item.slug, reviewState: next }),
    }).catch(() => {/* optimistic */});
    setReviews((prev) => prev.filter((r) => r.id !== id)); // leaves the review queue (back to Draft)
  };

  // C2 — Set for Campaign: assign this asset to a campaign + phase + due (all editable
  // later). Available to submitter or reviewer anytime; never blocks approval.
  const applyCampaign = (id: number, a: CampaignAssignment) => {
    const item = reviews.find((r) => r.id === id);
    if (!item?.slug) return;
    fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: clientSlug, id: item.slug, fields: { campaign: a.campaign, phase: a.phase, due: a.due } }),
    }).catch(() => {/* optimistic */});
    setReviews((prev) => prev.map((r) => (r.id === id ? { ...r, campaign: a.campaign } : r)));
    setCampaignPickFor(null);
  };

  // B4 — Delete: an ADMIN removes any review (the deliverable file). Same overflow slot as
  // Withdraw, role-scoped label; this is the destructive admin variant.
  const deleteItem = (id: number) => {
    const item = reviews.find((r) => r.id === id);
    if (!item?.slug) return;
    fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/delete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: clientSlug, id: item.slug }),
    }).catch(() => {/* optimistic */});
    setReviews((prev) => prev.filter((r) => r.id !== id));
  };

  const approveAndNextInline = (currentId: number) => {
    const cur = reviews.find((r) => r.id === currentId);
    if (cur?.kind === "decision") resolveDecision(currentId, "approved");
    else approveReal(currentId);
    const idx = filtered.findIndex((r) => r.id === currentId);
    // Reset any open Request-Changes sub-flow for the card being left behind
    setShowChangesFor(null);
    setShowSourceFor(null);
    setShowHistoryFor(null);
    if (idx === -1 || filtered.length <= 1) {
      // Nothing to advance to — collapse + show queue clear
      setExpandedId(null);
      setQueueCleared(true);
      return;
    }
    if (idx === filtered.length - 1) {
      // Last item approved — clear the queue rather than silently wrapping
      setExpandedId(null);
      setQueueCleared(true);
      return;
    }
    const nextId = filtered[idx + 1].id;
    setExpandedId(nextId);
    // Scroll the newly expanded card into view on next paint
    if (typeof window !== "undefined") {
      requestAnimationFrame(() => {
        document
          .getElementById(`review-card-${nextId}`)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  };

  // Approve the current slide-out item and advance slideOutId to the next
  // filtered item (reuses the same next-item logic the down arrow uses).
  const approveAndNextSlideOut = (currentId: number) => {
    const cur = reviews.find((r) => r.id === currentId);
    if (cur?.kind === "decision") resolveDecision(currentId, "approved");
    else approveReal(currentId);
    const idx = filtered.findIndex((r) => r.id === currentId);
    setSlideOutShowChanges(false);
    if (idx === -1 || idx >= filtered.length - 1) {
      // Last item (or not in current filter) — close panel + show queue clear
      setSlideOutId(null);
      setQueueCleared(true);
      return;
    }
    setSlideOutId(filtered[idx + 1].id);
  };

  // D3: post a plain comment on the open review without touching reviewState.
  // Mirrors the comment write inside submitChanges (deliverable/update, allowlisted
  // `comments` field) + the optimistic local-state pattern used by reopenItem.
  const postComment = (id: number, text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const item = reviews.find((r) => r.id === id);
    const newComment = { author: getCurrentMemberId() ?? "", at: new Date().toISOString(), text: trimmed };
    setReviews((prev) =>
      prev.map((r) => (r.id === id ? { ...r, comments: [...(r.comments || []), newComment] } : r))
    );
    setSlideOutCommentDraft("");
    if (item?.slug) {
      fetch(`${DOCS_API_DOMAIN}/api/agents/deliverable/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          client: clientSlug,
          id: item.slug,
          fields: { comments: [...(item.comments || []), newComment] },
        }),
      }).catch(() => {/* optimistic — local state already updated */});
    }
  };

  const TYPE_TABS: { key: TypeFilter; label: string }[] = [
    { key: "all", label: "All" },
    { key: "asset", label: "Assets" },
    { key: "decision", label: "Decisions" },
  ];

  const URGENCY_PILLS: { key: UrgencyFilter; label: string }[] = [
    { key: "all", label: "All" },
    { key: "urgent", label: "Urgent" },
    { key: "overdue", label: "Overdue" },
    { key: "due-today", label: "Due Today" },
    { key: "upcoming", label: "Upcoming" },
  ];

  return (
    <AppShellV8
      activeApp="reviews"
      breadcrumb={["Review Center"]}
      reviewCount={STATS.pending}
      headerActions={
        <Button label="Raise a Decision" type={ButtonTypes.OUTLINED} onClick={() => setCreateOpen(true)} />
      }
    >
      <div className="overflow-auto p-6">

        {/* ── Header + Inline Stats ── */}
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-xl font-semibold text-gray-900">Review Center</h1>
          <p className="text-xs text-gray-400">
            <span className="font-semibold text-gray-600">{STATS.pending}</span> pending
            <span className="mx-1.5 text-gray-300">&middot;</span>
            {STATS.urgent > 0 && (
              <>
                <span className="font-semibold text-orange-600">{STATS.urgent}</span> urgent
                <span className="mx-1.5 text-gray-300">&middot;</span>
              </>
            )}
            <span className="font-semibold text-red-600">{STATS.overdue}</span> overdue
            <span className="mx-1.5 text-gray-300">&middot;</span>
            <span className="font-semibold text-amber-600">{STATS.dueToday}</span> due today
          </p>
        </div>

        {/* ── Filter Bar (single row) ── */}
        <div className="mb-6 flex items-center gap-3">

          {/* Role toggle: My / All */}
          <div className="flex gap-0.5 rounded-lg bg-gray-100 p-0.5">
            <button
              onClick={() => setRoleView("my")}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-all duration-150 ${
                roleView === "my" ? "bg-gray-900 text-white shadow-sm" : "text-gray-500 hover:bg-gray-50"
              }`}
            >
              My Reviews
            </button>
            <button
              onClick={() => setRoleView("all")}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-all duration-150 ${
                roleView === "all" ? "bg-gray-900 text-white shadow-sm" : "text-gray-500 hover:bg-gray-50"
              }`}
            >
              All Reviews
            </button>
          </div>

          <div className="h-5 w-px bg-gray-200" />

          {/* Type toggle: All | Assets | Decisions */}
          <div className="flex gap-0.5 rounded-lg bg-gray-100 p-0.5">
            {TYPE_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setTypeFilter(tab.key)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition-all duration-150 ${
                  typeFilter === tab.key
                    ? "bg-gray-900 text-white shadow-sm"
                    : "text-gray-500 hover:bg-gray-50"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="h-5 w-px bg-gray-200" />

          {/* Urgency pills */}
          <div className="flex gap-1">
            {URGENCY_PILLS.map((pill) => (
              <button
                key={pill.key}
                onClick={() => setUrgencyFilter(pill.key)}
                className={`rounded-full px-2.5 py-0.5 text-[10px] font-medium transition-all duration-150 ${
                  urgencyFilter === pill.key
                    ? pill.key === "urgent"
                      ? "bg-orange-100 text-orange-700"
                      : pill.key === "overdue"
                        ? "bg-red-100 text-red-700"
                        : pill.key === "due-today"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-gray-900 text-white"
                    : "text-gray-500 hover:bg-gray-50"
                }`}
              >
                {pill.label}
              </button>
            ))}
          </div>

          <div className="h-5 w-px bg-gray-200" />

          {/* Filters button + popover */}
          <div className="relative">
            <button
              onClick={() => setFiltersOpen(!filtersOpen)}
              className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-all duration-150 ${
                activeFilterCount > 0
                  ? "border-blue-300 bg-blue-50 text-blue-700"
                  : "border-black/[0.06] bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z" />
              </svg>
              Filters
              {activeFilterCount > 0 && (
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-[9px] font-bold text-white">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Popover */}
            {filtersOpen && (
              <>
                {/* Backdrop */}
                <div className="fixed inset-0 z-10" onClick={() => setFiltersOpen(false)} />
                <div className="absolute left-0 top-full z-20 mt-1.5 w-64 rounded-xl border border-black/[0.06] bg-white p-4 shadow-lg ring-1 ring-black/[0.04]">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-700">Filter by</span>
                    {activeFilterCount > 0 && (
                      <button
                        onClick={() => {
                          setClientFilter("all");
                          setCampaignFilter("all");
                          setAssigneeFilter("all");
                        }}
                        className="text-[10px] font-medium text-blue-600 transition-all duration-150 hover:text-blue-800"
                      >
                        Clear all
                      </button>
                    )}
                  </div>

                  <div className="space-y-3">
                    {/* Client */}
                    <div>
                      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-400">Client</label>
                      <select
                        value={clientFilter}
                        onChange={(e) => setClientFilter(e.target.value)}
                        className="w-full rounded-lg border border-black/[0.06] bg-white px-2.5 py-1.5 text-xs text-gray-600 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                      >
                        <option value="all">All Clients</option>
                        {CLIENTS.map((c) => (
                          <option key={c.initials} value={c.initials}>{c.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* Campaign */}
                    <div>
                      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-400">Campaign</label>
                      <select
                        value={campaignFilter}
                        onChange={(e) => setCampaignFilter(e.target.value)}
                        className="w-full rounded-lg border border-black/[0.06] bg-white px-2.5 py-1.5 text-xs text-gray-600 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                      >
                        <option value="all">All Campaigns</option>
                        {campaignOptions.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>

                    {/* Assignee */}
                    <div>
                      <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-gray-400">Assignee</label>
                      <select
                        value={assigneeFilter}
                        onChange={(e) => setAssigneeFilter(e.target.value)}
                        className="w-full rounded-lg border border-black/[0.06] bg-white px-2.5 py-1.5 text-xs text-gray-600 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                      >
                        <option value="all">All Assignees</option>
                        {roster.map((p) => (
                          <option key={p.id} value={p.initials}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Mobile Preview toggle — hidden for trial (preview is a visual concept only).
              The divider + trigger are commented out so the button isn't exposed.
          <div className="h-5 w-px bg-gray-200" />
          <button
            onClick={() => setShowMobilePreview(!showMobilePreview)}
            className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-all duration-150 ${
              showMobilePreview
                ? "border-violet-300 bg-violet-50 text-violet-700"
                : "border-black/[0.06] bg-white text-gray-600 hover:bg-gray-50"
            }`}
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
            </svg>
            Mobile Preview
          </button>
          */}

          {/* Showing count (Raise a Decision moved to the topbar) — hidden until loaded
              so it never flashes a misleading "0 of 0" mid-load (F2 / AMB-352). */}
          {!reviewsLoading && (
            <span className="ml-auto text-[11px] text-gray-400">
              {filtered.length} of {visibleReviews.length}
            </span>
          )}
        </div>

        {/* ── Queue clear banner (after approving the last item) ── */}
        {queueCleared && (
          <div className="mb-5 flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 shadow-sm ring-1 ring-black/[0.04] transition-all duration-150">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            </span>
            <div className="flex-1">
              <p className="text-[13px] font-semibold text-emerald-800">Queue clear</p>
              <p className="text-xs text-emerald-600">You&apos;ve reviewed the last item in this queue. Nice work.</p>
            </div>
            <button
              onClick={() => setQueueCleared(false)}
              className="rounded-md p-1 text-emerald-500 transition-all duration-150 hover:bg-emerald-100 hover:text-emerald-700"
              title="Dismiss"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* ── Card Grid ── */}
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          {filtered.map((review) => {
            const isExpanded = expandedId === review.id;
            const kindBadge = KIND_BADGE[review.kind];
            const urgencyInfo = URGENCY_TEXT[review.urgency];
            const isShowingChanges = showChangesFor === review.id;
            const isShowingSource = showSourceFor === review.id;
            const isShowingHistory = showHistoryFor === review.id;
            // Author-status view: in "My Reviews", items I submitted should surface their
            // outcome (status badge + latest reviewer comment) without opening anything.
            const isMySubmission = roleView === "my" && review.submittedBy === currentMemberId;
            const stateBadge = reviewStateBadge(review.reviewState);
            const lastComment = latestComment(review.comments);
            // Reopen is available whenever an item is NOT in-review (approved / changes-requested).
            const isInReview = (review.reviewState || "in-review") === "in-review";

            return (
              <div
                key={review.id}
                id={`review-card-${review.id}`}
                className={`group overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-black/[0.04] transition-all duration-150 ${
                  review.urgency === "urgent" ? "border-l-4" : "border-l-[3px]"
                } ${URGENCY_BORDER[review.urgency]} ${
                  isExpanded ? "col-span-1 xl:col-span-2 shadow-md" : "hover:shadow"
                }`}
              >
                {/* ── Collapsed Card ── */}
                <div
                  className="cursor-pointer p-4"
                  data-testid={`review-card-${review.slug || review.id}`}
                  onClick={() => {
                    // D1 (UX-R5): every card — asset OR decision — opens the slide-out
                    // panel, which renders the clicked review's full content.
                    setSlideOutId(review.id);
                    setSlideOutShowChanges(false);
                    setSlideOutCommentDraft("");
                  }}
                >
                  {/* Row 1: Title + badges */}
                  <div className="mb-2.5 flex items-start gap-3">
                    {/* Client circle */}
                    <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${review.clientColor} text-[10px] font-bold text-white`}>
                      {review.clientInitials}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="truncate text-[13px] font-semibold text-gray-900">
                          {review.title}
                        </h3>
                        {review.unreviewed && (
                          <span className="h-2 w-2 shrink-0 rounded-full bg-blue-500" title="Unreviewed" />
                        )}
                        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${kindBadge.classes}`}>
                          {kindBadge.label}
                        </span>
                      </div>
                      <div className="mt-0.5 flex items-center gap-2 text-[11px] text-gray-400">
                        <span>{review.client}</span>
                        <span className="text-gray-300">/</span>
                        <span>{review.campaign}</span>
                      </div>
                    </div>

                    {/* Urgency label + inline expand chevron */}
                    <div className="flex shrink-0 items-center gap-2">
                      {review.urgency === "urgent" && (
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${URGENCY_BADGE.urgent}`}>
                          Urgent
                        </span>
                      )}
                      <span className={`text-[11px] font-medium ${urgencyInfo.color}`}>
                        {review.dueDate ? review.dueDate : "No date"}
                        {review.urgency === "overdue" && " (overdue)"}
                      </span>
                    </div>
                  </div>

                  {/* Row 2: Meta chips */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-500">
                    {/* Owner */}
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Owner</span>
                      <div className={`flex h-5 w-5 items-center justify-center rounded-full ${review.owner.color} text-[8px] font-bold text-white`}>
                        {review.owner.initials}
                      </div>
                      <span className="text-[13px]">{review.owner.name.split(" ")[0]}</span>
                    </div>

                    {/* Approvers */}
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Approver</span>
                      <div className="flex -space-x-1.5">
                        {review.approvers.map((a) => (
                          <div
                            key={a.initials}
                            className={`flex h-5 w-5 items-center justify-center rounded-full ${a.color} ring-2 ring-white text-[8px] font-bold text-white`}
                            title={a.name}
                          >
                            {a.initials}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Artifact count */}
                    {review.deliverableCount > 0 && (
                      <div className="flex items-center gap-1">
                        <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
                        </svg>
                        <span className="text-[11px] font-medium">{review.deliverableCount}</span>
                      </div>
                    )}

                    {/* Row actions (hover-visible) — open via the menu/card; approve &
                        request-changes happen in the slide-out (the fake quick-actions were removed). */}
                    <div className="ml-auto flex items-center gap-1 opacity-0 transition-all duration-150 group-hover:opacity-100">

                      {/* ⋯ overflow menu trigger */}
                      <button
                        onClick={(e) => openMenu(review.id, e)}
                        className="flex h-7 w-7 items-center justify-center rounded text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-600"
                        title="More actions"
                        aria-label="More actions"
                        data-testid={`review-menu-${review.slug || review.id}`}
                      >
                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                          <circle cx="5" cy="12" r="1.6" />
                          <circle cx="12" cy="12" r="1.6" />
                          <circle cx="19" cy="12" r="1.6" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                {/* ── Author status strip (My Reviews → my own submissions) ──
                   Shows the reviewer's outcome (status badge) + latest comment so the
                   author doesn't have to open anything to see what happened. */}
                {isMySubmission && (
                  <div className="border-t border-black/[0.04] bg-gray-50/60 px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Status</span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${stateBadge.classes}`}>
                        {stateBadge.label}
                      </span>
                      {!isInReview && (
                        <button
                          onClick={(e) => { e.stopPropagation(); reopenItem(review.id); }}
                          className="ml-auto rounded-md border border-black/[0.06] bg-white px-2 py-1 text-[10px] font-medium text-gray-600 transition-all duration-150 hover:bg-gray-100"
                          title="Move back to in-review"
                        >
                          Reopen
                        </button>
                      )}
                    </div>
                    {review.reviewState === "changes-requested" && lastComment && (
                      <p className="mt-1.5 text-[12px] leading-relaxed text-gray-600">
                        <span className="font-semibold text-gray-500">Latest feedback:</span> {lastComment.text}
                      </p>
                    )}
                  </div>
                )}

                {/* ── Reopen strip (non-author views: any item no longer in-review) ──
                   Gives reviewers a one-click path back to in-review after approve /
                   request-changes (the action was previously one-way). */}
                {!isMySubmission && !isInReview && (
                  <div className="flex items-center gap-2 border-t border-black/[0.04] bg-gray-50/60 px-4 py-2.5">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Status</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${stateBadge.classes}`}>
                      {stateBadge.label}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); reopenItem(review.id); }}
                      className="ml-auto rounded-md border border-black/[0.06] bg-white px-2 py-1 text-[10px] font-medium text-gray-600 transition-all duration-150 hover:bg-gray-100"
                      title="Move back to in-review"
                    >
                      Reopen
                    </button>
                  </div>
                )}

                {/* ── Expanded Detail ── */}
                {isExpanded && (
                  <div className="border-t border-black/[0.04] px-4 pb-5 pt-4" onClick={(e) => e.stopPropagation()}>
                    {/* Queue position indicator */}
                    {(() => {
                      const qIdx = filtered.findIndex((r) => r.id === review.id);
                      return qIdx >= 0 ? (
                        <div className="mb-3 flex items-center gap-2 text-[11px] font-medium text-gray-400">
                          <svg className="h-3.5 w-3.5 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 5.25h16.5m-16.5 4.5h16.5m-16.5 4.5h16.5m-16.5 4.5h16.5" />
                          </svg>
                          Review <span className="font-semibold text-gray-600">{qIdx + 1}</span> of <span className="font-semibold text-gray-600">{filtered.length}</span> in queue
                        </div>
                      ) : null;
                    })()}
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                      {/* Left: S.I.R. content */}
                      <div className="lg:col-span-2 space-y-4">

                        {/* Situation — only when present */}
                        {review.situation && (
                        <div>
                          <h4 className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-600">
                            <span className="flex h-5 w-5 items-center justify-center rounded bg-gray-800 text-[10px] font-bold text-white">S</span>
                            Situation
                          </h4>
                          <div className="rounded-lg bg-gray-50 p-3 text-[13px] leading-relaxed text-gray-600">
                            {review.situation}
                          </div>
                        </div>
                        )}

                        {/* Information — only when present */}
                        {review.information.length > 0 && (
                        <div>
                          <h4 className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-600">
                            <span className="flex h-5 w-5 items-center justify-center rounded bg-gray-800 text-[10px] font-bold text-white">I</span>
                            Information
                          </h4>
                          <ul className="space-y-1.5 rounded-lg bg-gray-50 p-3">
                            {review.information.map((info, i) => (
                              <li key={i} className="flex items-start gap-2 text-[13px] leading-relaxed text-gray-600">
                                <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gray-400" />
                                {info}
                              </li>
                            ))}
                          </ul>
                        </div>
                        )}

                        {/* Recommendation / Deliverable Preview / Decision Options — only when present */}
                        {(review.recommendation || (review.decisionOptions && review.decisionOptions.length > 0)) && (
                        <div>
                          <h4 className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-600">
                            <span className="flex h-5 w-5 items-center justify-center rounded bg-gray-800 text-[10px] font-bold text-white">R</span>
                            {review.kind === "decision" ? "Options" : "Recommendation"}
                          </h4>

                          {review.kind === "asset" && (
                            <div className="space-y-3">
                              {/* Artifact preview */}
                              {review.deliverablePreview && (
                                <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4">
                                  <div className="mb-2 flex items-center justify-between">
                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                                      </svg>
                                      {review.deliverablePreview}
                                    </div>
                                    <div className="flex gap-1.5">
                                      <Link
                                        to={review.slug ? `/agents/transcript-view?id=${encodeURIComponent(review.slug)}` : "/agents/transcript-view"}
                                        className="rounded-md border border-black/[0.06] px-2 py-1 text-[10px] font-medium text-gray-500 transition-all duration-150 hover:bg-gray-100"
                                      >
                                        Full Screen
                                      </Link>
                                    </div>
                                  </div>
                                  {/* Rendered markdown body */}
                                  <div className="max-h-96 overflow-auto rounded-md bg-white p-3 text-[13px] leading-relaxed text-gray-600 shadow-sm ring-1 ring-black/[0.04]">
                                    <Markdown remarkPlugins={[remarkGfm, remarkBreaks]} rehypePlugins={[rehypeRaw]} components={markdownComponents}>
                                      {review.recommendation}
                                    </Markdown>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {review.kind === "decision" && (review.decisionOptions?.length ? (
                            <div className="space-y-2">
                              {review.decisionOptions.map((opt, i) => (
                                <button
                                  key={i}
                                  onClick={() => setSelectedOption((prev) => ({ ...prev, [review.id]: i }))}
                                  className={`w-full rounded-lg border p-3 text-left transition-all duration-150 ${
                                    opt.recommended
                                      ? selectedOption[review.id] === i || (selectedOption[review.id] === undefined)
                                        ? "border-emerald-300 bg-emerald-50 ring-1 ring-emerald-200"
                                        : "border-emerald-200 bg-emerald-50/50"
                                      : selectedOption[review.id] === i
                                        ? "border-blue-300 bg-blue-50 ring-1 ring-blue-200"
                                        : "border-black/[0.06] bg-white hover:bg-gray-50"
                                  }`}
                                >
                                  <div className="mb-1 flex items-center gap-2">
                                    <span className="text-[13px] font-semibold text-gray-900">{opt.label}</span>
                                    {opt.recommended && (
                                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                                        Recommended
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-[13px] leading-relaxed text-gray-600">{opt.summary}</p>
                                </button>
                              ))}
                              {/* D4: Other / write-in */}
                              <button
                                onClick={() => setSelectedOption((prev) => ({ ...prev, [review.id]: -1 }))}
                                className={`w-full rounded-lg border border-dashed p-3 text-left text-[13px] font-medium transition-all duration-150 ${
                                  selectedOption[review.id] === -1 ? "border-blue-300 bg-blue-50 ring-1 ring-blue-200 text-gray-900" : "border-black/[0.12] bg-white text-gray-500 hover:bg-gray-50"
                                }`}
                              >
                                Other / write-in…
                              </button>
                              {selectedOption[review.id] === -1 && (
                                <textarea
                                  placeholder="Describe your decision…"
                                  value={decisionAnswer[review.id] || ""}
                                  onChange={(e) => setDecisionAnswer((prev) => ({ ...prev, [review.id]: e.target.value }))}
                                  className="w-full rounded-md border border-black/[0.06] bg-white p-2 text-[13px] text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                                  rows={2}
                                />
                              )}
                            </div>
                          ) : (
                            /* D4: open decision — free-text direction */
                            <textarea
                              placeholder="Provide direction…"
                              value={decisionAnswer[review.id] || ""}
                              onChange={(e) => setDecisionAnswer((prev) => ({ ...prev, [review.id]: e.target.value }))}
                              className="w-full rounded-md border border-black/[0.06] bg-white p-2.5 text-[13px] text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                              rows={3}
                            />
                          ))}
                        </div>
                        )}
                      </div>

                      {/* Right sidebar: actions + meta */}
                      <div className="space-y-4">
                        {/* Action buttons */}
                        <div className="rounded-lg border border-black/[0.06] p-3 space-y-2.5">
                          <h4 className="text-xs font-semibold text-gray-700">Actions</h4>
                          {/* Primary: Approve & Next — collapses this card, expands the next pending item */}
                          <button
                            onClick={() => approveAndNextInline(review.id)}
                            className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white transition-all duration-150 hover:bg-emerald-700 active:scale-[0.97]"
                          >
                            Approve &amp; Next
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                            </svg>
                          </button>
                          {/* Secondary: plain Approve (resolve without advancing) */}
                          <button
                            onClick={() => (review.kind === "decision" ? resolveDecision(review.id, "approved") : approveReal(review.id))}
                            className="w-full rounded-lg border border-emerald-200 px-3 py-1.5 text-[11px] font-semibold text-emerald-700 transition-all duration-150 hover:bg-emerald-50"
                          >
                            Approve only
                          </button>
                          <button
                            onClick={() => {
                              setShowChangesFor(isShowingChanges ? null : review.id);
                              setChangeComment("");
                            }}
                            className="w-full rounded-lg bg-amber-500 px-3 py-2 text-xs font-semibold text-white transition-all duration-150 hover:bg-amber-600"
                          >
                            Request Changes
                          </button>

                          {/* Request Changes expanded — apply-universally lives here only (single checkbox) */}
                          {isShowingChanges && (
                            <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                              <textarea
                                placeholder="Describe the changes needed..."
                                value={changeComment}
                                onChange={(e) => setChangeComment(e.target.value)}
                                className="w-full rounded-md border border-amber-200 bg-white p-2 text-[13px] text-gray-700 placeholder:text-gray-400 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-amber-300"
                                rows={3}
                              />
                              <label className="flex items-center gap-2 text-xs text-gray-600">
                                <input
                                  type="checkbox"
                                  checked={applyUniversally}
                                  onChange={(e) => setApplyUniversally(e.target.checked)}
                                  className="h-3.5 w-3.5 rounded border-gray-300 text-amber-500 focus:ring-amber-400"
                                />
                                Apply universally — train AI for all future outputs of this type
                              </label>
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Send to</span>
                                <button type="button" onClick={() => setRecipientPicker("card")} className="flex-1 rounded-md border border-amber-200 bg-white px-2 py-1 text-left text-xs text-gray-600 transition-all duration-150 hover:bg-amber-50 focus:outline-none focus:ring-1 focus:ring-amber-300">
                                  {sendToRecipients.length ? sendToRecipients.map((s) => personFor(s).name).join(", ") : "Select reviewer(s)…"}
                                </button>
                              </div>
                              <button disabled={!changeComment.trim()} onClick={() => { submitChanges(review.id, sendToRecipients, changeComment); setShowChangesFor(null); setChangeComment(""); setSendToRecipients([]); setApplyUniversally(false); }} className="w-full rounded-md bg-amber-600 px-2 py-1.5 text-xs font-medium text-white transition-all duration-150 hover:bg-amber-700 disabled:opacity-40 disabled:cursor-not-allowed">
                                Submit Feedback
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Source — meetings trail */}
                        <div className="rounded-lg border border-black/[0.06] p-3">
                          <button
                            onClick={() => setShowSourceFor(isShowingSource ? null : review.id)}
                            className="flex w-full items-center justify-between text-xs font-semibold text-gray-700 transition-all duration-150"
                          >
                            <span className="flex items-center gap-1.5">
                              <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 01-.923 1.785A5.969 5.969 0 006 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337z" />
                              </svg>
                              Source ({review.meetings.length})
                            </span>
                            <svg className={`h-3.5 w-3.5 text-gray-400 transition-all duration-150 ${isShowingSource ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                            </svg>
                          </button>
                          {isShowingSource && (
                            <div className="mt-2 space-y-1.5">
                              {review.meetings.map((m, i) => (
                                <Link
                                  key={i}
                                  to={m.href}
                                  className="flex items-center gap-2 rounded-md px-2 py-1.5 text-xs text-gray-600 transition-all duration-150 hover:bg-gray-50"
                                >
                                  <svg className="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                  </svg>
                                  <span className="font-medium">{m.title}</span>
                                  <span className="text-gray-400">{m.date}</span>
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Revision History */}
                        <div className="rounded-lg border border-black/[0.06] p-3">
                          <button
                            onClick={() => setShowHistoryFor(isShowingHistory ? null : review.id)}
                            className="flex w-full items-center justify-between text-xs font-semibold text-gray-700 transition-all duration-150"
                          >
                            <span className="flex items-center gap-1.5">
                              <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              History ({review.revisions.length})
                            </span>
                            <svg className={`h-3.5 w-3.5 text-gray-400 transition-all duration-150 ${isShowingHistory ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                            </svg>
                          </button>
                          {isShowingHistory && (
                            <div className="mt-2 space-y-2">
                              {review.revisions.map((rev) => (
                                <div key={rev.version} className="relative border-l-2 border-gray-200 pl-3">
                                  <div className="absolute -left-[5px] top-1 h-2 w-2 rounded-full bg-gray-300" />
                                  <div className="flex items-center gap-2 text-[10px] text-gray-400">
                                    <span className="font-semibold">v{rev.version}</span>
                                    <span>{rev.when}</span>
                                    <span>by {rev.by}</span>
                                  </div>
                                  <p className="mt-0.5 text-[13px] text-gray-600">{rev.change}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Meta summary */}
                        <div className="rounded-lg bg-gray-50 p-3 space-y-2 text-[13px] text-gray-500">
                          <div className="flex justify-between">
                            <span className="text-xs text-gray-400">Type</span>
                            <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${kindBadge.classes}`}>{kindBadge.label}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-xs text-gray-400">Client</span>
                            <span className="flex items-center gap-1.5">
                              <span className={`h-2 w-2 rounded-full ${review.clientColor}`} />
                              {review.client}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-xs text-gray-400">Campaign</span>
                            <span>{review.campaign}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-xs text-gray-400">Due</span>
                            <span className={urgencyInfo.color}>{review.dueDate || "No date"}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-xs text-gray-400">Owner</span>
                            <span className="flex items-center gap-1.5">
                              <span className={`flex h-4 w-4 items-center justify-center rounded-full ${review.owner.color} text-[7px] font-bold text-white`}>{review.owner.initials}</span>
                              {review.owner.name.split(" ")[0]}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-xs text-gray-400">Approvers</span>
                            <div className="flex -space-x-1">
                              {review.approvers.map((a) => (
                                <div key={a.initials} className={`flex h-4 w-4 items-center justify-center rounded-full ${a.color} ring-1 ring-white text-[7px] font-bold text-white`} title={a.name}>
                                  {a.initials}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Empty state */}
        {reviewsLoading && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-[13px] text-gray-400">Loading reviews…</p>
          </div>
        )}
        {!reviewsLoading && filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <svg className="mb-3 h-12 w-12 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-[13px] font-medium text-gray-500">No items match your filters</p>
            <p className="mt-1 text-xs text-gray-400">Try adjusting the type, client, or urgency filters</p>
            <button
              onClick={() => {
                setTypeFilter("all");
                setClientFilter("all");
                setCampaignFilter("all");
                setUrgencyFilter("all");
                setAssigneeFilter("all");
              }}
              className="mt-3 rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600 transition-all duration-150 hover:bg-gray-200"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
         SLIDE-OUT PANEL (Asset Review Detail)
         ═══════════════════════════════════════════════════════════════════════ */}
      {slideOutId !== null && (() => {
        const slideOutItem = reviews.find((r) => r.id === slideOutId);
        if (!slideOutItem) return null;
        const currentIndex = filtered.findIndex((r) => r.id === slideOutId);
        const totalCount = filtered.length;
        const canGoUp = currentIndex > 0;
        const canGoDown = currentIndex < totalCount - 1 && currentIndex >= 0;
        const displayIndex = currentIndex >= 0 ? currentIndex + 1 : 1;

        return (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[2px] transition-opacity duration-200"
              onClick={() => setSlideOutId(null)}
            />

            {/* Panel — AMB-403: responsive width. Wide screens get a capped width (so the
                drawer doesn't dominate); small screens can take most of the viewport. */}
            <div
              className="fixed right-0 top-0 z-50 flex h-full w-[92vw] max-w-[1080px] flex-col border-l border-black/[0.06] bg-white shadow-2xl transition-all duration-300 sm:w-[85vw] lg:w-[64vw] xl:w-[56vw]"
              style={{ animation: "slideInRight 0.25s ease-out" }}
            >
              {/* Panel Header */}
              <div className="flex items-center justify-between border-b border-black/[0.04] px-5 py-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${slideOutItem.clientColor} text-[9px] font-bold text-white`}>
                    {slideOutItem.clientInitials}
                  </div>
                  <div className="min-w-0">
                    <h2 className="truncate text-[13px] font-semibold text-gray-900">{slideOutItem.title}</h2>
                    <p className="text-[11px] text-gray-400">{slideOutItem.client} / {slideOutItem.campaign}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {/* Width toggle removed (D2 — panel is always fully expanded). */}
                  {/* Close */}
                  <button
                    onClick={() => { setSlideOutId(null); setSlideOutShowChanges(false); setSlideOutCommentDraft(""); }}
                    className="rounded-md p-1.5 text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-600"
                    title="Close"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Navigation arrows */}
              <div className="flex items-center justify-between border-b border-black/[0.04] px-5 py-2">
                <span className="flex items-center gap-1.5 text-[11px] font-medium text-gray-400">
                  <svg className="h-3.5 w-3.5 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 5.25h16.5m-16.5 4.5h16.5m-16.5 4.5h16.5m-16.5 4.5h16.5" />
                  </svg>
                  Review <span className="font-semibold text-gray-600">{displayIndex}</span> of <span className="font-semibold text-gray-600">{totalCount}</span>
                </span>
                <div className="flex items-center gap-1">
                  <button
                    disabled={!canGoUp}
                    onClick={() => {
                      if (canGoUp) { setSlideOutId(filtered[currentIndex - 1].id); setSlideOutShowChanges(false); }
                    }}
                    className={`rounded-md p-1 transition-all duration-150 ${
                      canGoUp
                        ? "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                        : "cursor-not-allowed text-gray-200"
                    }`}
                    title="Previous item"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                    </svg>
                  </button>
                  <button
                    disabled={!canGoDown}
                    onClick={() => {
                      if (canGoDown) { setSlideOutId(filtered[currentIndex + 1].id); setSlideOutShowChanges(false); }
                    }}
                    className={`rounded-md p-1 transition-all duration-150 ${
                      canGoDown
                        ? "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                        : "cursor-not-allowed text-gray-200"
                    }`}
                    title="Next item"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Content area — scrollable */}
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
                {/* Urgency + Due Date badge */}
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${URGENCY_BADGE[slideOutItem.urgency]}`}>
                    {URGENCY_TEXT[slideOutItem.urgency].label}
                  </span>
                  <span className="text-[11px] text-gray-400">
                    {slideOutItem.dueDate ? `Due ${slideOutItem.dueDate}` : "No due date"}
                  </span>
                </div>

                {/* Split layout when expanded, stacked when collapsed */}
                <div className={panelExpanded ? "grid grid-cols-5 gap-6" : ""}>

                  {/* Left side: S.I.R. content */}
                  <div className={`space-y-5 ${panelExpanded ? "col-span-2" : ""}`}>
                    {/* Situation — only when present */}
                    {slideOutItem.situation && (
                    <div>
                      <h4 className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-600">
                        <span className="flex h-5 w-5 items-center justify-center rounded bg-gray-800 text-[10px] font-bold text-white">S</span>
                        Situation
                      </h4>
                      <div className="rounded-lg bg-gray-50 p-3 text-[13px] leading-relaxed text-gray-600">
                        {slideOutItem.situation}
                      </div>
                    </div>
                    )}

                    {/* Impact — why it matters (shown between Situation and Recommendation) */}
                    {slideOutItem.impact && (
                    <div>
                      <h4 className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-600">
                        <span className="flex h-5 w-5 items-center justify-center rounded bg-gray-800 text-[10px] font-bold text-white">I</span>
                        Impact
                      </h4>
                      <div className="rounded-lg bg-gray-50 p-3 text-[13px] leading-relaxed text-gray-600">
                        {slideOutItem.impact}
                      </div>
                    </div>
                    )}

                    {/* Information — only when present */}
                    {slideOutItem.information.length > 0 && (
                    <div>
                      <h4 className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-600">
                        <span className="flex h-5 w-5 items-center justify-center rounded bg-gray-800 text-[10px] font-bold text-white">I</span>
                        Information
                      </h4>
                      <ul className="space-y-1.5 rounded-lg bg-gray-50 p-3">
                        {slideOutItem.information.map((info, i) => (
                          <li key={i} className="flex items-start gap-2 text-[13px] leading-relaxed text-gray-600">
                            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gray-400" />
                            {info}
                          </li>
                        ))}
                      </ul>
                    </div>
                    )}

                    {/* Recommendation (only shown stacked when NOT expanded) */}
                    {!panelExpanded && (
                      <div>
                        <h4 className="mb-1.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-600">
                          <span className="flex h-5 w-5 items-center justify-center rounded bg-gray-800 text-[10px] font-bold text-white">R</span>
                          Recommendation
                        </h4>

                        {/* Rendered markdown body */}
                        <div className="max-h-[60vh] overflow-auto rounded-lg bg-gray-50 p-3 text-[13px] leading-relaxed text-gray-600">
                          <Markdown remarkPlugins={[remarkGfm, remarkBreaks]} rehypePlugins={[rehypeRaw]} components={markdownComponents}>
                            {slideOutItem.recommendation}
                          </Markdown>
                        </div>
                      </div>
                    )}

                    {/* Artifact info (stacked only) — opens the document's Knowledge Base
                        page (BUG-12: previously a dead "… preview · N file" label with no way
                        to open it; now a real link to the full document). */}
                    {!panelExpanded && slideOutItem.deliverablePreview && (
                      <Link
                        to={`/agents/transcript-view?id=${encodeURIComponent(slideOutItem.id)}`}
                        className="flex w-full items-center gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-2.5 text-left transition-all duration-150 hover:border-blue-400 hover:bg-blue-50/40"
                      >
                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                        </svg>
                        <span className="text-[13px] text-gray-600">View the {slideOutItem.deliverablePreview.replace(/ preview$/i, "").toLowerCase()}</span>
                        <span className="ml-auto flex items-center gap-1 text-[11px] font-medium text-blue-600">
                          {slideOutItem.deliverableCount} file{slideOutItem.deliverableCount !== 1 ? "s" : ""}
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
                        </span>
                      </Link>
                    )}

                    {/* Revision history */}
                    {slideOutItem.revisions.length > 0 && (
                      <div>
                        <h4 className="mb-2 text-xs font-semibold text-gray-500">Revision History</h4>
                        <div className="space-y-1.5">
                          {slideOutItem.revisions.map((rev) => (
                            <div key={rev.version} className="relative border-l-2 border-gray-200 pl-3">
                              <div className="absolute -left-[5px] top-1 h-2 w-2 rounded-full bg-gray-300" />
                              <div className="flex items-center gap-2 text-[10px] text-gray-400">
                                <span className="font-semibold">v{rev.version}</span>
                                <span>{rev.when}</span>
                                <span>by {rev.by}</span>
                              </div>
                              <p className="mt-0.5 text-[13px] text-gray-600">{rev.change}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right side: decisions render the Options voting UI; assets render
                      the real document content. (D1 — both kinds open in this panel.) */}
                  {slideOutItem.kind === "decision" ? (
                    <div className="col-span-3 flex flex-col">
                      <h4 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-600">
                        <span className="flex h-5 w-5 items-center justify-center rounded bg-gray-800 text-[10px] font-bold text-white">R</span>
                        Options
                      </h4>
                      {slideOutItem.decisionOptions?.length ? (
                        <div className="space-y-2">
                          {slideOutItem.decisionOptions.map((opt, i) => (
                            <button
                              key={i}
                              onClick={() => setSelectedOption((prev) => ({ ...prev, [slideOutItem.id]: i }))}
                              className={`w-full rounded-lg border p-3 text-left transition-all duration-150 ${
                                opt.recommended
                                  ? selectedOption[slideOutItem.id] === i || (selectedOption[slideOutItem.id] === undefined)
                                    ? "border-emerald-300 bg-emerald-50 ring-1 ring-emerald-200"
                                    : "border-emerald-200 bg-emerald-50/50"
                                  : selectedOption[slideOutItem.id] === i
                                    ? "border-blue-300 bg-blue-50 ring-1 ring-blue-200"
                                    : "border-black/[0.06] bg-white hover:bg-gray-50"
                              }`}
                            >
                              <div className="mb-1 flex items-center gap-2">
                                <span className="text-[13px] font-semibold text-gray-900">{opt.label}</span>
                                {opt.recommended && (
                                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                                    Recommended
                                  </span>
                                )}
                              </div>
                              <p className="text-[13px] leading-relaxed text-gray-600">{opt.summary}</p>
                            </button>
                          ))}
                          {/* D4: Other / write-in */}
                          <button
                            onClick={() => setSelectedOption((prev) => ({ ...prev, [slideOutItem.id]: -1 }))}
                            className={`w-full rounded-lg border border-dashed p-3 text-left text-[13px] font-medium transition-all duration-150 ${
                              selectedOption[slideOutItem.id] === -1 ? "border-blue-300 bg-blue-50 ring-1 ring-blue-200 text-gray-900" : "border-black/[0.12] bg-white text-gray-500 hover:bg-gray-50"
                            }`}
                          >
                            Other / write-in…
                          </button>
                          {selectedOption[slideOutItem.id] === -1 && (
                            <textarea
                              placeholder="Describe your decision…"
                              value={decisionAnswer[slideOutItem.id] || ""}
                              onChange={(e) => setDecisionAnswer((prev) => ({ ...prev, [slideOutItem.id]: e.target.value }))}
                              className="w-full rounded-md border border-black/[0.06] bg-white p-2 text-[13px] text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                              rows={2}
                            />
                          )}
                        </div>
                      ) : (
                        /* D4: open decision — free-text direction */
                        <textarea
                          placeholder="Provide direction…"
                          value={decisionAnswer[slideOutItem.id] || ""}
                          onChange={(e) => setDecisionAnswer((prev) => ({ ...prev, [slideOutItem.id]: e.target.value }))}
                          className="w-full rounded-md border border-black/[0.06] bg-white p-2.5 text-[13px] text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                          rows={4}
                        />
                      )}
                    </div>
                  ) : (
                    <div className="col-span-3 flex flex-col">
                      <div className="mb-2 flex items-center justify-between">
                        <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-600">
                          <span className="flex h-5 w-5 items-center justify-center rounded bg-gray-800 text-[10px] font-bold text-white">R</span>
                          Document
                        </h4>
                        <Link to={`/agents/transcript-view?id=${encodeURIComponent(slideOutItem.id)}`} className="text-[11px] font-medium text-blue-600 hover:underline">Open full document →</Link>
                      </div>
                      {slideBody === null ? (
                        <p className="text-[13px] text-gray-400">Loading content…</p>
                      ) : slideBody.replace(/^\s*#.*$/m, "").trim() ? (
                        <div className="flex-1 overflow-auto rounded-lg border border-gray-200 bg-white p-5 text-[13px] leading-relaxed text-gray-700 ring-1 ring-black/[0.04] [&_h1]:mb-2 [&_h1]:text-base [&_h1]:font-bold [&_h2]:mb-1.5 [&_h2]:mt-3 [&_h2]:text-sm [&_h2]:font-semibold [&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-0.5">
                          <Markdown remarkPlugins={[remarkGfm, remarkBreaks]} rehypePlugins={[rehypeRaw]} components={markdownComponents}>{slideBody}</Markdown>
                        </div>
                      ) : (
                        <div className="flex-1 overflow-auto rounded-lg border border-gray-200 bg-white p-5 text-[13px] leading-relaxed text-gray-700 ring-1 ring-black/[0.04]">
                          <Markdown remarkPlugins={[remarkGfm, remarkBreaks]} rehypePlugins={[rehypeRaw]} components={markdownComponents}>{slideOutItem.recommendation || "_No content yet._"}</Markdown>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* ── Comments (D3 / UX-R1) — post a comment without going through
                    Request Changes. Appends to `comments` via deliverable/update and
                    does NOT change reviewState. ── */}
                <div className="border-t border-black/[0.04] pt-4">
                  <h4 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-gray-600">
                    <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
                    </svg>
                    Comments {slideOutItem.comments && slideOutItem.comments.length > 0 ? `(${slideOutItem.comments.length})` : ""}
                  </h4>
                  {slideOutItem.comments && slideOutItem.comments.length > 0 ? (
                    <div className="mb-3 space-y-2">
                      {slideOutItem.comments.map((c, i) => (
                        <div key={i} className="rounded-lg bg-gray-50 p-2.5">
                          <div className="mb-0.5 flex items-center gap-2 text-[10px] text-gray-400">
                            <span className="font-semibold text-gray-500">{c.author}</span>
                            {c.at && <span>{shortDate(c.at)}</span>}
                          </div>
                          <p className="text-[13px] leading-relaxed text-gray-600">{c.text}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="mb-3 text-[12px] text-gray-400">No comments yet.</p>
                  )}
                  <div className="flex items-start gap-2">
                    <textarea
                      data-testid="slideout-comment-input"
                      placeholder="Add a comment…"
                      value={slideOutCommentDraft}
                      onChange={(e) => setSlideOutCommentDraft(e.target.value)}
                      className="flex-1 rounded-md border border-black/[0.06] bg-white p-2 text-[13px] text-gray-700 placeholder:text-gray-400 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                      rows={2}
                    />
                    <button
                      data-testid="slideout-post-comment"
                      disabled={!slideOutCommentDraft.trim()}
                      onClick={() => postComment(slideOutItem.id, slideOutCommentDraft)}
                      className="rounded-lg bg-gray-900 px-3 py-2 text-xs font-semibold text-white transition-all duration-150 hover:bg-gray-800 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Post
                    </button>
                  </div>
                </div>
              </div>

              {/* Action buttons — fixed at bottom */}
              <div className="border-t border-black/[0.04] px-5 py-4 space-y-2">
                {/* Reopen — only when this item is no longer in-review (approved / changes-requested) */}
                {(slideOutItem.reviewState || "in-review") !== "in-review" && (
                  <div className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Status</span>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${reviewStateBadge(slideOutItem.reviewState).classes}`}>
                      {reviewStateBadge(slideOutItem.reviewState).label}
                    </span>
                    <button
                      onClick={() => reopenItem(slideOutItem.id)}
                      className="ml-auto rounded-md border border-black/[0.06] bg-white px-2 py-1 text-[10px] font-medium text-gray-600 transition-all duration-150 hover:bg-gray-100"
                      title="Move back to in-review"
                    >
                      Reopen
                    </button>
                  </div>
                )}
                {/* A4: approve buttons show only to an assigned reviewer (decisions are
                    resolvable by any reviewer). Non-assignees can see the item but not approve. */}
                {(slideOutItem.kind === "decision" || canApprove(slideOutItem, currentMemberId)) && (
                  <>
                {/* Primary: Approve & Next — approves and advances to the next filtered item */}
                <button
                  data-testid="slideout-approve-next"
                  onClick={() => approveAndNextSlideOut(slideOutItem.id)}
                  className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-gray-900 px-3 py-2.5 text-xs font-semibold text-white transition-all duration-150 hover:bg-gray-800 active:scale-[0.97]"
                >
                  Approve &amp; Next
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </button>
                  </>
                )}
                <div className="flex gap-2">
                  {(slideOutItem.kind === "decision" || canApprove(slideOutItem, currentMemberId)) && (
                  <button
                    data-testid="slideout-approve"
                    onClick={() => { (slideOutItem.kind === "decision" ? resolveDecision(slideOutItem.id, "approved") : approveReal(slideOutItem.id)); setSlideOutId(null); }}
                    className="flex-1 rounded-lg border border-black/[0.1] px-3 py-2 text-[11px] font-semibold text-gray-700 transition-all duration-150 hover:bg-gray-50"
                  >
                    Approve only
                  </button>
                  )}
                  <button
                    data-testid="slideout-request-changes"
                    onClick={() => {
                      setSlideOutShowChanges(!slideOutShowChanges);
                      setSlideOutComment("");
                    }}
                    className={`flex-1 rounded-lg border px-3 py-2 text-[11px] font-semibold transition-all duration-150 ${
                      slideOutShowChanges
                        ? "border-gray-300 bg-gray-100 text-gray-800"
                        : "border-black/[0.1] text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    Request Changes
                  </button>
                </div>

                {/* apply-universally lives inside the Request Changes flow only (single checkbox) */}
                {slideOutShowChanges && (
                  <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <textarea
                      placeholder="Describe the changes needed..."
                      value={slideOutComment}
                      onChange={(e) => setSlideOutComment(e.target.value)}
                      className="w-full rounded-md border border-amber-200 bg-white p-2 text-[13px] text-gray-700 placeholder:text-gray-400 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-amber-300"
                      rows={3}
                    />
                    <label className="flex items-center gap-2 text-xs text-gray-600">
                      <input
                        type="checkbox"
                        checked={slideOutApplyUniversally}
                        onChange={(e) => setSlideOutApplyUniversally(e.target.checked)}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-amber-500 focus:ring-amber-400"
                      />
                      Apply universally — train AI for all future outputs of this type
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Send to</span>
                      <button type="button" onClick={() => setRecipientPicker("slideout")} className="flex-1 rounded-md border border-amber-200 bg-white px-2 py-1 text-left text-xs text-gray-600 transition-all duration-150 hover:bg-amber-50 focus:outline-none focus:ring-1 focus:ring-amber-300">
                        {slideOutRecipients.length ? slideOutRecipients.map((s) => personFor(s).name).join(", ") : "Select reviewer(s)…"}
                      </button>
                    </div>
                    <button data-testid="slideout-submit-changes" disabled={!slideOutComment.trim()} onClick={() => { if (slideOutId != null) submitChanges(slideOutId, slideOutRecipients, slideOutComment); setSlideOutId(null); setSlideOutShowChanges(false); setSlideOutRecipients([]); setSlideOutApplyUniversally(false); setSlideOutComment(""); }} className="w-full rounded-md bg-gray-900 px-2 py-1.5 text-xs font-medium text-white transition-all duration-150 hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed">
                      Submit Feedback
                    </button>
                  </div>
                )}

                {!slideOutShowChanges && (
                  <div className="flex items-center gap-2 pt-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Route to</span>
                    <button type="button" onClick={() => setRecipientPicker("slideout")} className="flex-1 rounded-md border border-black/[0.06] bg-white px-2 py-1.5 text-left text-xs text-gray-600 transition-all duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/30">
                      {slideOutRecipients.length ? slideOutRecipients.map((s) => personFor(s).name).join(", ") : "Select reviewer(s)…"}
                    </button>
                  </div>
                )}
                {/* C2: Set for Campaign — a prominent sibling action, available anytime, never
                    required before approval. Outline (secondary) per the monochrome system. */}
                {slideOutItem.kind !== "decision" && (
                  <button
                    onClick={() => setCampaignPickFor(slideOutItem.id)}
                    className="w-full rounded-lg border border-black/[0.1] px-3 py-2 text-[12px] font-medium text-gray-700 transition-all duration-150 hover:bg-gray-50"
                  >
                    {slideOutItem.campaign ? "Change campaign" : "+ Add to campaign"}
                  </button>
                )}
              </div>
            </div>

            {/* Slide-in animation */}
            <style>{`
              @keyframes slideInRight {
                from { transform: translateX(100%); opacity: 0.8; }
                to { transform: translateX(0); opacity: 1; }
              }
            `}</style>
          </>
        );
      })()}

      {/* ═══════════════════════════════════════════════════════════════════════
         MOBILE PREVIEW MOCKUP
         ═══════════════════════════════════════════════════════════════════════ */}
      {showMobilePreview && (() => {
        const pendingItems = visibleReviews.filter((r) => r.unreviewed);
        const pendingCount = pendingItems.length;
        const totalMobile = visibleReviews.length;

        return (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[3px]"
              onClick={() => { setShowMobilePreview(false); setMobileExpanded(false); }}
            />

            <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => { setShowMobilePreview(false); setMobileExpanded(false); }}>
              {/* Close hint */}
              <button
                onClick={() => { setShowMobilePreview(false); setMobileExpanded(false); }}
                className="absolute right-8 top-8 rounded-full bg-white/90 p-2 text-gray-500 shadow-lg transition-all duration-150 hover:bg-white hover:text-gray-700"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Label */}
              <div className="absolute top-8 left-1/2 -translate-x-1/2 rounded-full bg-white/90 px-4 py-1.5 shadow-lg">
                <span className="text-xs font-semibold text-gray-600">Mobile Review Experience — Visual Concept</span>
              </div>

              {/* iPhone frame */}
              <div
                className="relative"
                onClick={(e) => e.stopPropagation()}
                style={{ width: 375, height: 812 }}
              >
                {/* Phone outer border */}
                <div className="absolute inset-0 rounded-[48px] border-[3px] border-gray-800 bg-gray-900 shadow-lg">
                  {/* Notch */}
                  <div className="absolute left-1/2 top-0 -translate-x-1/2 h-[30px] w-[150px] rounded-b-2xl bg-gray-900" />

                  {/* Screen area */}
                  <div className="absolute inset-[3px] overflow-hidden rounded-[44px] bg-gray-50">
                    {/* Status bar */}
                    <div className="flex items-center justify-between bg-white px-6 pb-1 pt-12">
                      <span className="text-[11px] font-semibold text-gray-900">9:41</span>
                      <div className="flex items-center gap-1">
                        {/* Signal */}
                        <div className="flex items-end gap-[2px]">
                          <div className="h-[4px] w-[3px] rounded-sm bg-gray-900" />
                          <div className="h-[6px] w-[3px] rounded-sm bg-gray-900" />
                          <div className="h-[8px] w-[3px] rounded-sm bg-gray-900" />
                          <div className="h-[10px] w-[3px] rounded-sm bg-gray-900" />
                        </div>
                        {/* WiFi */}
                        <svg className="ml-1 h-3 w-3 text-gray-900" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 18l-1.5-1.5a2.12 2.12 0 013 0L12 18zm-3-3l-1.5-1.5a5.12 5.12 0 017 0L15 15a3.12 3.12 0 00-6 0zm-3-3L4.5 10.5a8.12 8.12 0 0111 0L15 12a6.12 6.12 0 00-9 0z" />
                        </svg>
                        {/* Battery */}
                        <div className="ml-1 flex h-[10px] w-[22px] items-center rounded-[3px] border border-gray-900 px-[2px]">
                          <div className="h-[5px] w-[14px] rounded-[1px] bg-gray-900" />
                        </div>
                      </div>
                    </div>

                    {/* App header */}
                    <div className="bg-white px-5 pb-3 pt-2">
                      <h3 className="text-lg font-bold text-gray-900">Review Center</h3>
                      <p className="mt-0.5 text-[11px] text-gray-400">
                        <span className="font-semibold text-orange-600">{pendingCount} of {totalMobile}</span> pending review
                      </p>
                    </div>

                    {/* Swipe instructions */}
                    <div className="mx-5 mb-3 flex items-center justify-between rounded-lg bg-gray-100 px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-emerald-600 font-semibold">Swipe right</span>
                        <svg className="h-3 w-3 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12h15m0 0l-6.75-6.75M19.5 12l-6.75 6.75" />
                        </svg>
                        <span className="text-[10px] text-gray-400">approve</span>
                      </div>
                      <div className="h-3 w-px bg-gray-300" />
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-gray-400">reject</span>
                        <svg className="h-3 w-3 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12h-15m0 0l6.75 6.75M4.5 12l6.75-6.75" />
                        </svg>
                        <span className="text-[10px] text-red-600 font-semibold">Swipe left</span>
                      </div>
                    </div>

                    {/* Notification-style cards — stacked with slight rotation on top card */}
                    <div className="relative mx-5" style={{ height: 380 }}>
                      {/* Background cards for depth */}
                      <div className="absolute inset-x-2 top-4 h-full rounded-2xl bg-white shadow-sm ring-1 ring-black/[0.04]" style={{ transform: "rotate(2deg)" }} />
                      <div className="absolute inset-x-1 top-2 h-full rounded-2xl bg-white shadow-sm ring-1 ring-black/[0.04]" style={{ transform: "rotate(1deg)" }} />

                      {/* Top card / Expanded card view */}
                      {!mobileExpanded ? (
                        <>
                          <div className="absolute inset-x-0 top-0 rounded-2xl bg-white shadow-lg ring-1 ring-black/[0.06] p-4" style={{ transform: "rotate(-1deg)" }}>
                            {/* Expand button */}
                            <button
                              onClick={() => setMobileExpanded(true)}
                              className="absolute right-3 top-3 z-10 flex h-7 w-7 items-center justify-center rounded-lg bg-gray-100 text-gray-500 transition-all duration-150 hover:bg-gray-200 hover:text-gray-700"
                              title="Expand card"
                            >
                              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                              </svg>
                            </button>

                            {/* Swipe overlay hints */}
                            <div className="absolute -left-1 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-full bg-red-500/10">
                              <svg className="h-4 w-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </div>
                            <div className="absolute -right-1 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500/10">
                              <svg className="h-4 w-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                              </svg>
                            </div>

                            {/* Card content — first pending review item */}
                            {pendingItems[0] && (
                              <>
                                <div className="mb-3 flex items-start gap-3">
                                  <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${pendingItems[0].clientColor} text-[10px] font-bold text-white`}>
                                    {pendingItems[0].clientInitials}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <h4 className="text-[13px] font-semibold text-gray-900">{pendingItems[0].title}</h4>
                                    <p className="mt-0.5 text-[11px] text-gray-400">{pendingItems[0].client}</p>
                                  </div>
                                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold ${URGENCY_BADGE[pendingItems[0].urgency]}`}>
                                    {URGENCY_TEXT[pendingItems[0].urgency].label}
                                  </span>
                                </div>

                                <div className="mb-3 rounded-lg bg-gray-50 p-3 text-[11px] leading-relaxed text-gray-600">
                                  {pendingItems[0].situation.substring(0, 120)}...
                                </div>

                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className={`rounded-full px-2 py-0.5 text-[9px] font-semibold ${KIND_BADGE[pendingItems[0].kind].classes}`}>
                                      {KIND_BADGE[pendingItems[0].kind].label}
                                    </span>
                                    {pendingItems[0].deliverableCount > 0 && (
                                      <span className="flex items-center gap-1 text-[10px] text-gray-400">
                                        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
                                        </svg>
                                        {pendingItems[0].deliverableCount}
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-[10px] text-gray-400">{pendingItems[0].dueDate || "No date"}</span>
                                </div>
                              </>
                            )}
                          </div>

                          {/* Remaining cards preview (visible beneath) */}
                          {pendingItems.slice(1, 4).map((item, i) => (
                            <div
                              key={item.id}
                              className="absolute inset-x-0 rounded-2xl bg-white ring-1 ring-black/[0.04] p-3 opacity-60"
                              style={{ top: 200 + (i * 52), zIndex: -1 - i }}
                            >
                              <div className="flex items-center gap-3">
                                <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${item.clientColor} text-[8px] font-bold text-white`}>
                                  {item.clientInitials}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <h5 className="truncate text-[11px] font-semibold text-gray-700">{item.title}</h5>
                                  <p className="text-[10px] text-gray-400">{item.client}</p>
                                </div>
                                <span className={`text-[9px] font-medium ${URGENCY_TEXT[item.urgency].color}`}>
                                  {item.dueDate || "No date"}
                                </span>
                              </div>
                            </div>
                          ))}
                        </>
                      ) : (
                        /* Expanded fullscreen card within phone frame */
                        <div className="absolute inset-0 z-10 flex flex-col bg-white rounded-b-[44px] overflow-hidden">
                          {/* Back button */}
                          <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-100">
                            <button
                              onClick={() => setMobileExpanded(false)}
                              className="flex items-center gap-1 text-xs font-medium text-blue-600 transition-all duration-150"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                              </svg>
                              Back to stack
                            </button>
                          </div>

                          {/* Scrollable content */}
                          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                            {pendingItems[0] && (
                              <>
                                {/* Header */}
                                <div className="flex items-start gap-3">
                                  <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${pendingItems[0].clientColor} text-[10px] font-bold text-white`}>
                                    {pendingItems[0].clientInitials}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <h4 className="text-[13px] font-semibold text-gray-900">{pendingItems[0].title}</h4>
                                    <p className="mt-0.5 text-[11px] text-gray-400">{pendingItems[0].client} / {pendingItems[0].campaign}</p>
                                  </div>
                                </div>

                                {/* Urgency + meta */}
                                <div className="flex items-center gap-2">
                                  <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold ${URGENCY_BADGE[pendingItems[0].urgency]}`}>
                                    {URGENCY_TEXT[pendingItems[0].urgency].label}
                                  </span>
                                  <span className={`rounded-full px-2 py-0.5 text-[9px] font-semibold ${KIND_BADGE[pendingItems[0].kind].classes}`}>
                                    {KIND_BADGE[pendingItems[0].kind].label}
                                  </span>
                                  <span className="text-[10px] text-gray-400">{pendingItems[0].dueDate || "No date"}</span>
                                </div>

                                {/* S — Situation */}
                                <div>
                                  <h5 className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                                    <span className="flex h-4 w-4 items-center justify-center rounded bg-gray-800 text-[8px] font-bold text-white">S</span>
                                    Situation
                                  </h5>
                                  <div className="rounded-lg bg-gray-50 p-2.5 text-[11px] leading-relaxed text-gray-600">
                                    {pendingItems[0].situation}
                                  </div>
                                </div>

                                {/* I — Information */}
                                <div>
                                  <h5 className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                                    <span className="flex h-4 w-4 items-center justify-center rounded bg-gray-800 text-[8px] font-bold text-white">I</span>
                                    Information
                                  </h5>
                                  <ul className="space-y-1 rounded-lg bg-gray-50 p-2.5">
                                    {pendingItems[0].information.map((info, i) => (
                                      <li key={i} className="flex items-start gap-1.5 text-[11px] leading-relaxed text-gray-600">
                                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-gray-400" />
                                        {info}
                                      </li>
                                    ))}
                                  </ul>
                                </div>

                                {/* R — Recommendation / Preview */}
                                <div>
                                  <h5 className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                                    <span className="flex h-4 w-4 items-center justify-center rounded bg-gray-800 text-[8px] font-bold text-white">R</span>
                                    {pendingItems[0].kind === "decision" ? "Options" : "Preview"}
                                  </h5>
                                  <div className="rounded-lg bg-gray-50 p-2.5 text-[11px] leading-relaxed text-gray-600 whitespace-pre-wrap">
                                    {pendingItems[0].recommendation || pendingItems[0].situation.substring(0, 80) + "..."}
                                  </div>
                                </div>
                              </>
                            )}
                          </div>

                          {/* Action buttons pinned at bottom */}
                          <div className="border-t border-gray-100 px-4 py-3 space-y-2">
                            <div className="flex gap-2">
                              <button className="flex-1 rounded-lg bg-emerald-600 py-2.5 text-[11px] font-semibold text-white transition-all duration-150 hover:bg-emerald-700">
                                Approve
                              </button>
                              <button className="flex-1 rounded-lg bg-amber-500 py-2.5 text-[11px] font-semibold text-white transition-all duration-150 hover:bg-amber-600">
                                Changes
                              </button>
                              <button className="rounded-lg border border-red-200 px-4 py-2.5 text-[11px] font-semibold text-red-600 transition-all duration-150 hover:bg-red-50">
                                Reject
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Voice note floating button */}
                    <div className="absolute bottom-8 right-8 flex flex-col items-center gap-1.5">
                      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 shadow-lg shadow-emerald-500/30">
                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
                        </svg>
                      </div>
                      <span className="text-[9px] font-medium text-gray-500">Leave feedback</span>
                    </div>

                    {/* Bottom home indicator */}
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 h-[5px] w-[134px] rounded-full bg-gray-900" />
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      })()}

      {/* ═══════════════════════════════════════════════════════════════════════
         ⋯ OVERFLOW MENU  (Edit / Archive / Dismiss·Withdraw)
         ═══════════════════════════════════════════════════════════════════════ */}
      {menuFor !== null && (() => {
        const item = reviews.find((r) => r.id === menuFor);
        if (!item) return null;
        const isDecision = item.kind === "decision";
        // B4 role-scoped action (same slot, different label):
        //  - the submitter can Withdraw their own still-open item back to Draft;
        //  - an admin can Delete any review outright.
        const canWithdraw = !isDecision && item.submittedBy === currentMemberId
          && (item.reviewState === "in-review" || item.reviewState === "changes-requested");
        const menuItems: OverflowMenuItem[] = [
          { label: "Edit", onClick: () => setEditingId(item.id) },
          ...(canWithdraw
            ? [{ label: "Withdraw", onClick: () => withdrawItem(item.id), destructive: true }]
            : []),
          ...(isDecision
            ? [{ label: "Dismiss / Withdraw", onClick: () => archiveItem(item.id, "Dismissed"), destructive: true }]
            : []),
          { label: "Archive", onClick: () => archiveItem(item.id, "Archived"), destructive: true },
          ...(!isDecision && isAdmin(currentMemberId)
            ? [{ label: "Delete", onClick: () => deleteItem(item.id), destructive: true }]
            : []),
        ];
        return <OverflowMenu items={menuItems} anchor={menuAnchor} onClose={() => setMenuFor(null)} />;
      })()}

      {/* C2 — Set-for-Campaign picker (campaign + phase + due), opened from the drawer. */}
      {campaignPickFor !== null && (() => {
        const item = reviews.find((r) => r.id === campaignPickFor);
        return (
          <SetForCampaignPicker
            current={{ campaign: item?.campaign || "" }}
            onConfirm={(a) => applyCampaign(campaignPickFor, a)}
            onClose={() => setCampaignPickFor(null)}
          />
        );
      })()}

      {/* AMB-390: send-back recipient picker — the SAME shared EntityPicker (multi) that
          Send-for-Review uses, so choosing who to route changes to is multi-recipient + DRY. */}
      {recipientPicker && (
        <EntityPicker
          title="Send back — pick recipient(s)"
          endpoint="/members"
          multiple
          selected={recipientPicker === "card" ? sendToRecipients : slideOutRecipients}
          mapItem={(m) => ({ id: m.id, label: m.name, sublabel: m.login ? undefined : "contact" })}
          confirmLabel="Choose"
          onConfirm={(ids) => { recipientPicker === "card" ? setSendToRecipients(ids) : setSlideOutRecipients(ids); setRecipientPicker(null); }}
          onClose={() => setRecipientPicker(null)}
        />
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
         CREATE DECISION MODAL
         ═══════════════════════════════════════════════════════════════════════ */}
      {createOpen && (
        <DecisionModal
          mode="create"
          campaigns={campaignOptions}
          initial={{
            title: "",
            clientInitials: CLIENTS[0].initials,
            campaign: campaignOptions[0] || "",
            ownerInitials: defaultOwner?.initials ?? "",
            urgency: "upcoming",
            situation: "",
            information: "",
            recommendation: "",
          }}
          onCancel={() => setCreateOpen(false)}
          onSave={createDecision}
        />
      )}

      {/* ═══════════════════════════════════════════════════════════════════════
         EDIT MODAL
         ═══════════════════════════════════════════════════════════════════════ */}
      {editingId !== null && (() => {
        const item = reviews.find((r) => r.id === editingId);
        if (!item) return null;
        return (
          <DecisionModal
            mode="edit"
            campaigns={campaignOptions}
            initial={{
              title: item.title,
              clientInitials: item.clientInitials,
              campaign: item.campaign,
              ownerInitials: item.owner.initials,
              urgency: item.urgency,
              situation: item.situation,
              information: item.information.join("\n"),
              recommendation: item.recommendation,
            }}
            onCancel={() => setEditingId(null)}
            onSave={(draft) => saveEdit(item.id, draft)}
          />
        );
      })()}

      {/* ═══════════════════════════════════════════════════════════════════════
         TOAST  (Archived / Dismissed · Undo)
         ═══════════════════════════════════════════════════════════════════════ */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[80] -translate-x-1/2 transition-all duration-150">
          <div className="flex items-center gap-3 rounded-xl bg-gray-900 px-4 py-2.5 text-[13px] text-white shadow-2xl ring-1 ring-black/[0.2]">
            <span>{toast.message.replace(" · Undo", "")}</span>
            <span className="text-gray-500">·</span>
            <button
              onClick={() => { toast.undo(); setToast(null); }}
              className="font-semibold text-emerald-400 transition-all duration-150 hover:text-emerald-300 active:scale-[0.97]"
            >
              Undo
            </button>
          </div>
        </div>
      )}

    </AppShellV8>
  );
}
